import { memo, useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown, Popover, PopoverBody, Modal } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimesCircle,
  faWandMagicSparkles,
  faChevronLeft,
  faChevronRight
} from "@fortawesome/free-solid-svg-icons";
import { unzipSync } from "fflate";
import { Upload } from "@aws-sdk/lib-storage";
import { GetObjectCommand, S3Client } from "@aws-sdk/client-s3";
import { EventSourcePolyfill } from "event-source-polyfill";
import { getCokkie, getTenantId } from "../../login/Login";
import { ChipsetIcon, CloseIcon,DownArrowIcon,FullCycleIcon, SendMessageIcon, UploadIcon } from "../../Common/SvgIcons/WorkflowIcons";
import './Models.scss'
import getDataFromApi from "../../../components/getDataFromApi";
import { ImageCanvas } from "../../../components/modal/NavPanes/CanvaImage";
import EvaluationTargetHardwareMenu from "./EvaluationTargetHardwareMenu";
import { StyledTooltip } from "../label/annotation/EditorView/EditorTopNavigationBar/EditorTopNavigationBar";

const REGION = "us-east-2";

const TASK_TYPES = {
  VISUAL_QUESTION_ANSWERING: "Visual Question Answering",
  ZERO_SHOT_CLASSIFICATION: "ZeroShot Classification",
  TEXT_GENERATION: "Text Generation",
  ZERO_SHOT_DETECTION: "ZeroShot Detection",
};

const TEXT_PROMPT_REQUIRED_MESSAGE = "Text prompt is required to start evaluation.";
export const LLM_DRAFT_SESSION_ID = "__llm_draft_evaluation__";

const STREAM_STATUS = {
  ANALYSING: "Analysing...",
  DONE: "Status-Done",
};
const NO_RESPONSE_MESSAGE = "No Response";
const MAX_VQA_IMAGES = 3;
const EXAMPLE_MODALITY_OPTIONS = [
  {
    value: "text",
    label: "text",
    heading: "Try with these sample Text examples",
  },
  {
    value: "image",
    label: "image",
    heading: "Try with these sample Image examples",
  },
  {
    value: "text+image",
    label: "images + text prompt",
    heading: "Try with these sample Images + Text prompt examples",
  },
];

const ACTIVE_STREAMS_BY_EVAL_ID = new Map();
const HUB_SAMPLE_EXAMPLES_CACHE = new Map();
const HUB_SAMPLE_EXAMPLES_PENDING = new Map();

const normalizeContextNumber = (value) => {
  const match = String(value ?? "").match(/\d+/);
  const contextNumber = Number(match?.[0]);
  return Number.isFinite(contextNumber) && contextNumber > 0 ? contextNumber : null;
};

const normalizeAvailableContexts = (values, fallback = 1) => {
  const contexts = (Array.isArray(values) ? values : [])
    .map(normalizeContextNumber)
    .filter(Boolean);

  return [...new Set(contexts.length ? contexts : [fallback])].sort((a, b) => a - b);
};

const getContextMetadata = (payload, evalId) => {
  const nestedRows = Array.isArray(payload?.device_table?.data)
    ? payload.device_table.data
    : [];
  const candidates = [payload, ...nestedRows].filter(
    (item) => item && typeof item === "object" && !Array.isArray(item)
  );
  const matchingCandidate = candidates.find(
    (item) =>
      item?.context_chat_summaries &&
      typeof item.context_chat_summaries === "object" &&
      !Array.isArray(item.context_chat_summaries) &&
      [item?._id, item?.eval_id, item?.evaluation_id].some(
        (candidateId) => candidateId && String(candidateId) === String(evalId)
      )
  );
  const metadataSource =
    matchingCandidate ||
    candidates.find(
      (item) =>
        item?.context_chat_summaries &&
        typeof item.context_chat_summaries === "object" &&
        !Array.isArray(item.context_chat_summaries)
    );
  const contextChatSummaries =
    metadataSource?.context_chat_summaries &&
    typeof metadataSource.context_chat_summaries === "object" &&
    !Array.isArray(metadataSource.context_chat_summaries)
      ? metadataSource.context_chat_summaries
      : {};
  const summaryContexts = Object.keys(contextChatSummaries)
    .map(normalizeContextNumber)
    .filter(Boolean);

  return {
    contextChatSummaries,
    availableContexts: normalizeAvailableContexts(
      [
        ...(Array.isArray(metadataSource?.available_contexts)
          ? metadataSource.available_contexts
          : []),
        ...summaryContexts,
      ],
      summaryContexts[0] || 1
    ),
  };
};

const MODEL_TASK_TYPE_BY_NAME = {
  "llava-OneVision-7b": TASK_TYPES.VISUAL_QUESTION_ANSWERING,
  florence2: TASK_TYPES.VISUAL_QUESTION_ANSWERING,
  smolvlm2: TASK_TYPES.VISUAL_QUESTION_ANSWERING,
  longclip: TASK_TYPES.ZERO_SHOT_CLASSIFICATION,
  xclip: TASK_TYPES.ZERO_SHOT_CLASSIFICATION,
  tinyclip: TASK_TYPES.ZERO_SHOT_CLASSIFICATION,
  "InternVL-3.5-1B": TASK_TYPES.VISUAL_QUESTION_ANSWERING,
  "DeepSeek-R1-Distill-Qwen-7B": TASK_TYPES.TEXT_GENERATION,
  "llava-OneVision-qwen2-7b": TASK_TYPES.TEXT_GENERATION,
  owlvit: TASK_TYPES.ZERO_SHOT_DETECTION,
};

const getHubSampleImageName = (imageUrl, modelName, index) => {
  try {
    const pathname = new URL(imageUrl).pathname;
    return decodeURIComponent(pathname.split("/").filter(Boolean).pop());
  } catch {
    return `${modelName}_${index + 1}.jpg`;
  }
};

const normalizeHubSamples = (payload, modelName) =>
  (Array.isArray(payload?.samples) ? payload.samples : [])
    .map((sample, index) => {
      const modality = String(sample?.modality || "").trim().toLowerCase();
      const includesText = modality === "text" || modality === "text+image";
      const includesImage = modality === "image" || modality === "text+image";
      const prompt = includesText ? String(sample?.input_prompt || "").trim() : "";
      const imageUrls = includesImage
        ? [...new Set([
            ...(Array.isArray(sample?.image_urls) ? sample.image_urls : []),
            sample?.image_url,
          ]
            .map((imageUrl) => String(imageUrl || "").trim())
            .filter(Boolean))]
        : [];
      const images = imageUrls.map((imageUrl, imageIndex) => ({
        src: imageUrl,
        name: getHubSampleImageName(imageUrl, modelName, imageIndex),
        label: prompt || `Image ${imageIndex + 1}`,
      }));

      if ((!prompt && !images.length) || !["text", "image", "text+image"].includes(modality)) {
        return null;
      }

      return {
        id: `hub-sample-${index}`,
        modality,
        prompt,
        label: prompt || `Image ${index + 1}`,
        ...(images.length
          ? {
              image: images[0],
              images,
            }
          : {}),
      };
    })
    .filter(Boolean);

const getCachedHubSamples = (samplesUrl, modelName, downloadAsset) => {
  if (HUB_SAMPLE_EXAMPLES_CACHE.has(samplesUrl)) {
    return Promise.resolve(HUB_SAMPLE_EXAMPLES_CACHE.get(samplesUrl));
  }

  if (HUB_SAMPLE_EXAMPLES_PENDING.has(samplesUrl)) {
    return HUB_SAMPLE_EXAMPLES_PENDING.get(samplesUrl);
  }

  const pendingRequest = downloadAsset(samplesUrl)
    .then((blob) => blob.text())
    .then((content) => JSON.parse(content))
    .then((payload) => {
      const samples = normalizeHubSamples(payload, modelName);
      HUB_SAMPLE_EXAMPLES_CACHE.set(samplesUrl, samples);
      return samples;
    })
    .finally(() => {
      HUB_SAMPLE_EXAMPLES_PENDING.delete(samplesUrl);
    });

  HUB_SAMPLE_EXAMPLES_PENDING.set(samplesUrl, pendingRequest);
  return pendingRequest;
};


const normalizeTaskType = (value) => {
  const normalized = String(value || "")
    .trim()
    .replace(/[\s_-]+/g, "")
    .toLowerCase();

  switch (normalized) {
    case "visualquestionanswering":
    case "image-text-to-text":
    case "imagetexttotext":
      return TASK_TYPES.VISUAL_QUESTION_ANSWERING;
    case "zeroshotclassification":
      return TASK_TYPES.ZERO_SHOT_CLASSIFICATION;
    case "textgeneration":
      return TASK_TYPES.TEXT_GENERATION;
    case "zeroshotdetection":
      return TASK_TYPES.ZERO_SHOT_DETECTION;
    default:
      return value || "";
  }
};

const IMAGE_PREVIEW_CACHE = new Map();
const IMAGE_PREVIEW_PENDING = new Map();
const IMAGE_PREVIEW_QUEUE = [];
let activePreviewJobs = 0;
const MAX_ACTIVE_PREVIEW_JOBS = 1;

const getRenderableImageUrl = (url) => {
  if (!url) return "";
  const src = String(url);
  if (
    src.startsWith("http") ||
    src.startsWith("data:image") ||
    src.startsWith("blob:")
  ) {
    return src;
  }
  return `data:image/jpeg;base64,${src}`;
};

const getImageUrls = (value) => {
  const values = Array.isArray(value) ? value.flat(Infinity) : [value];

  return [...new Set(
    values
      .map((url) => String(url || "").trim())
      .filter(Boolean)
  )];
};

const canvasToObjectUrl = (canvas, quality = 0.72) =>
  new Promise((resolve) => {
    try {
      canvas.toBlob(
        (blob) => resolve(blob ? URL.createObjectURL(blob) : ""),
        "image/jpeg",
        quality
      );
    } catch {
      resolve("");
    }
  });

const createPreviewFromSource = async (src, maxSize) => {
  if (!src || typeof document === "undefined") {
    return { url: src || "", sourceWidth: null, sourceHeight: null };
  }

  const drawPreview = async (imageLike, sourceWidth, sourceHeight) => {
    const scale = Math.min(maxSize / sourceWidth, maxSize / sourceHeight, 1);

    if (scale >= 1) {
      return { url: src, sourceWidth, sourceHeight };
    }

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(sourceWidth * scale));
    canvas.height = Math.max(1, Math.round(sourceHeight * scale));

    const ctx = canvas.getContext("2d", { alpha: false });
    ctx.drawImage(imageLike, 0, 0, canvas.width, canvas.height);

    const url = await canvasToObjectUrl(canvas);
    return { url: url || src, sourceWidth, sourceHeight };
  };

  try {
    if (typeof createImageBitmap === "function") {
      const response = await fetch(src);
      const blob = await response.blob();
      const bitmap = await createImageBitmap(blob);
      const result = await drawPreview(bitmap, bitmap.width, bitmap.height);
      bitmap.close?.();
      return result;
    }
  } catch {
    // Fall back to image element decoding below.
  }

  return new Promise((resolve) => {
    const image = new Image();
    image.decoding = "async";
    if (/^https?:\/\//i.test(src)) {
      image.crossOrigin = "anonymous";
    }

    image.onload = async () => {
      const sourceWidth = image.naturalWidth || image.width;
      const sourceHeight = image.naturalHeight || image.height;

      if (!sourceWidth || !sourceHeight) {
        resolve({ url: src, sourceWidth: null, sourceHeight: null });
        return;
      }

      try {
        resolve(await drawPreview(image, sourceWidth, sourceHeight));
      } catch {
        resolve({ url: src, sourceWidth, sourceHeight });
      }
    };

    image.onerror = () => {
      resolve({ url: src, sourceWidth: null, sourceHeight: null });
    };

    image.src = src;
  });
};

const runPreviewQueue = () => {
  if (activePreviewJobs >= MAX_ACTIVE_PREVIEW_JOBS) return;

  const next = IMAGE_PREVIEW_QUEUE.shift();
  if (!next) return;

  activePreviewJobs += 1;

  createPreviewFromSource(next.src, next.maxSize)
    .then((preview) => {
      IMAGE_PREVIEW_CACHE.set(next.key, preview);
      next.resolve(preview);
    })
    .catch(() => {
      const fallback = { url: next.src, sourceWidth: null, sourceHeight: null };
      IMAGE_PREVIEW_CACHE.set(next.key, fallback);
      next.resolve(fallback);
    })
    .finally(() => {
      IMAGE_PREVIEW_PENDING.delete(next.key);
      activePreviewJobs -= 1;
      runPreviewQueue();
    });
};

const getImagePreviewCacheKey = (src, maxSize) => {
  const value = String(src || "");

  if (value.length <= 2048) {
    return `${maxSize}:${value}`;
  }

  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
  }

  return `${maxSize}:${value.length}:${hash}:${value.slice(0, 64)}:${value.slice(-64)}`;
};

const getQueuedImagePreview = (src, maxSize) => {
  const key = getImagePreviewCacheKey(src, maxSize);

  if (IMAGE_PREVIEW_CACHE.has(key)) {
    return Promise.resolve(IMAGE_PREVIEW_CACHE.get(key));
  }

  if (IMAGE_PREVIEW_PENDING.has(key)) {
    return IMAGE_PREVIEW_PENDING.get(key);
  }

  const promise = new Promise((resolve) => {
    IMAGE_PREVIEW_QUEUE.push({ key, src, maxSize, resolve });
    runPreviewQueue();
  });

  IMAGE_PREVIEW_PENDING.set(key, promise);
  return promise;
};

const useImagePreview = (src, maxSize, immediateUrl = "") => {
  const [preview, setPreview] = useState(() =>
    immediateUrl ? { url: immediateUrl, sourceWidth: null, sourceHeight: null } : null
  );

  useEffect(() => {
    let cancelled = false;

    if (!src) {
      setPreview(null);
      return () => {
        cancelled = true;
      };
    }

    if (immediateUrl) {
      setPreview({ url: immediateUrl, sourceWidth: null, sourceHeight: null });
      return () => {
        cancelled = true;
      };
    }

    setPreview(null);

    getQueuedImagePreview(src, maxSize).then((nextPreview) => {
      if (!cancelled) {
        setPreview(nextPreview);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [src, maxSize, immediateUrl]);

  return preview;
};

const ImagePreviewPlaceholder = ({ width = "100%", height = 180 }) => (
  <div
    className="rounded"
    style={{
      width,
      height,
      background: "var(--bg-color)",
      border: "1px solid var(--glass-color)"
    }}
  />
);

const LongclipBubblePreview = memo(function LongclipBubblePreview({ img, imageKey, onOpen }) {
  const originalUrl = useMemo(() => getRenderableImageUrl(img?.imageUrl), [img?.imageUrl]);
  const preview = useImagePreview(originalUrl, 120, img?.thumbUrl || "");

  const handleClick = useCallback(() => {
    onOpen(originalUrl);
  }, [onOpen, originalUrl]);

  if (!preview?.url) {
    return <ImagePreviewPlaceholder width={60} height={60} />;
  }

  return (
    <img
      key={imageKey}
      src={preview.url}
      alt={img?.imageName || "input image"}
      loading="eager"
      decoding="async"
      style={{
        width: "60px",
        height: "60px",
        objectFit: "cover",
        borderRadius: "4px",
        cursor: "pointer",
      }}
      onClick={handleClick}
    />
  );
});

const LlmBubbleImagePreview = memo(function LlmBubbleImagePreview({
  src,
  thumbUrl = "",
  alt = "input image",
  onOpen,
  size = 150,
}) {
  const originalUrl = useMemo(() => getRenderableImageUrl(src), [src]);
  const immediateUrl = useMemo(() => getRenderableImageUrl(thumbUrl), [thumbUrl]);
  const preview = useImagePreview(originalUrl, 320, immediateUrl);

  const handleClick = useCallback(() => {
    if (originalUrl) {
      onOpen(originalUrl);
    }
  }, [onOpen, originalUrl]);

  if (!preview?.url) {
    return <ImagePreviewPlaceholder width={size} height={size} />;
  }

  return (
    <img
      src={preview.url}
      alt={alt}
      loading="lazy"
      decoding="async"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        borderRadius: "8px",
        objectFit: "cover",
        cursor: "pointer",
      }}
      onClick={handleClick}
    />
  );
});

const LongclipResponsePreview = memo(function LongclipResponsePreview({
  img,
  imageKey,
  taskType,
  modelName,
  onImageLoad,
  onOpen
}) {
  const originalUrl = useMemo(() => getRenderableImageUrl(img?.imageUrl), [img?.imageUrl]);
  const isZeroShotDetection = taskType === TASK_TYPES.ZERO_SHOT_DETECTION;
  const isZeroShotClassification = taskType === TASK_TYPES.ZERO_SHOT_CLASSIFICATION;
  const preview = useImagePreview(originalUrl, isZeroShotDetection ? 720 : 860);
  const boxes = img?.boxes?.[0];
  const scores = useMemo(() => img?.scores || [], [img?.scores]);

  const handleLoad = useCallback(() => {
    onImageLoad(imageKey);
  }, [imageKey, onImageLoad]);

  const handleClick = useCallback(() => {
    onOpen(originalUrl, boxes, scores);
  }, [boxes, onOpen, originalUrl, scores]);

  if (!preview?.url) {
    return <ImagePreviewPlaceholder height={220} />;
  }

  if (isZeroShotClassification) {
    return (
      <img
        src={preview.url}
        alt={img?.imageName || "response image"}
        loading="eager"
        decoding="async"
        onLoad={handleLoad}
        onClick={handleClick}
        style={{
          width: "100%",
          height: "auto",
          borderRadius: "6px",
          objectFit: "contain",
          background: "var(--bg-color)",
          cursor: "pointer",
          display: "block"
        }}
      />
    );
  }

  return (
    <ImageCanvas
      key={`${img?.imageName || originalUrl}-${imageKey}`}
      img={{
        url: preview.url,
        sourceWidth: preview.sourceWidth,
        sourceHeight: preview.sourceHeight,
        boxes,
        landmarks: img?.landmarks,
        score: scores
      }}
      onLoad={handleLoad}
      onClick={handleClick}
      modelName={modelName}
    />
  );
});

const getReadableError = (error, fallback = "Something went wrong while evaluating this prompt.") => {
  if (!error) return fallback;
  if (typeof error === "string") return error;
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    error?.statusText ||
    fallback
  );
};

const safeJsonParse = (value) => {
  if (typeof value !== "string") return { ok: true, value };

  try {
    return { ok: true, value: JSON.parse(value) };
  } catch (error) {
    return { ok: false, value, error };
  }
};

const parseCoordinateArray = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return null;

  const parsed = safeJsonParse(value.trim());
  return parsed.ok && Array.isArray(parsed.value) ? parsed.value : null;
};

const extractFlorenceArrayField = (text, fieldNames) => {
  const fieldMatch = String(text || "").match(
    new RegExp(`["'](?:${fieldNames.join("|")})["']\\s*:`)
  );
  if (!fieldMatch) return null;

  const start = text.indexOf("[", fieldMatch.index + fieldMatch[0].length);
  if (start === -1) return null;

  let depth = 0;
  for (let index = start; index < text.length; index += 1) {
    if (text[index] === "[") depth += 1;
    if (text[index] === "]") depth -= 1;
    if (depth === 0) return text.slice(start, index + 1);
  }

  return null;
};

const isCoordinatePair = (value) =>
  Array.isArray(value) &&
  value.length >= 2 &&
  Number.isFinite(Number(value[0])) &&
  Number.isFinite(Number(value[1]));

const isBoundingBox = (value) =>
  Array.isArray(value) &&
  value.length >= 4 &&
  value.slice(0, 4).every((coordinate) => Number.isFinite(Number(coordinate)));

const getFlorenceSpatialPayload = (value) => {
  let payload = value;

  if (typeof payload === "string") {
    const trimmed = payload.trim();
    const json = safeJsonParse(trimmed);

    if (json.ok && json.value && typeof json.value === "object") {
      payload = json.value;
    } else {
      // Florence streaming responses are currently serialized as Python dicts.
      const imageUrl = trimmed.match(/["']image_url["']\s*:\s*["']([^"']+)["']/)?.[1];
      const boxValue = extractFlorenceArrayField(trimmed, ["box", "bbox"]);
      const segmentValue = extractFlorenceArrayField(trimmed, ["segment", "segmentation"]);

      if (!imageUrl || (!boxValue && !segmentValue)) return null;
      payload = {
        image_url: imageUrl,
        ...(boxValue ? { box: boxValue } : {}),
        ...(segmentValue ? { segment: segmentValue } : {}),
      };
    }
  }

  if (!payload || typeof payload !== "object" || Array.isArray(payload)) return null;

  const imageUrl = payload.image_url || payload.imageUrl || "";
  const rawBox = parseCoordinateArray(payload.box ?? payload.bbox);
  const rawSegment = parseCoordinateArray(payload.segment ?? payload.segmentation);
  const boxCandidates = isBoundingBox(rawBox) ? [rawBox] : rawBox;
  const segmentCandidates =
    Array.isArray(rawSegment) && rawSegment.every(isCoordinatePair)
      ? [rawSegment]
      : rawSegment;
  const boxes = Array.isArray(boxCandidates)
    ? boxCandidates
        .filter(isBoundingBox)
        .map((box) => box.slice(0, 4).map(Number))
    : [];
  const segments = Array.isArray(segmentCandidates)
    ? segmentCandidates
        .filter((polygon) => Array.isArray(polygon) && polygon.length >= 2)
        .map((polygon) => polygon.filter(isCoordinatePair).map(([x, y]) => [Number(x), Number(y)]))
        .filter((polygon) => polygon.length >= 2)
    : [];

  if (!imageUrl || (boxes.length === 0 && segments.length === 0)) return null;

  return {
    imageUrl,
    box: boxes[0] || null,
    boxes,
    segment: segments[0] || [],
    segments,
  };
};

const FlorenceSpatialPreview = memo(function FlorenceSpatialPreview({
  spatial,
  onOpen,
  isModal = false,
}) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const imageUrl = useMemo(
    () => getRenderableImageUrl(spatial?.imageUrl),
    [spatial?.imageUrl]
  );

  const sourceWidth = dimensions.width || 1;
  const sourceHeight = dimensions.height || 1;
  const boxes = Array.isArray(spatial?.boxes) && spatial.boxes.length > 0
    ? spatial.boxes
    : (spatial?.box ? [spatial.box] : []);
  const segments = Array.isArray(spatial?.segments) && spatial.segments.length > 0
    ? spatial.segments
    : (Array.isArray(spatial?.segment) && spatial.segment.length > 0 ? [spatial.segment] : []);

  if (!imageUrl) return null;

  return (
    <div
      style={{
        position: "relative",
        width: isModal ? "min(90vw, 900px)" : "min(100%, 520px)",
        lineHeight: 0,
        cursor: onOpen ? "pointer" : "default",
      }}
      onClick={() => onOpen?.(imageUrl, spatial)}
    >
      <img
        src={imageUrl}
        alt="Florence spatial response"
        onLoad={(event) => {
          const image = event.currentTarget;
          setDimensions({
            width: image.naturalWidth || image.width,
            height: image.naturalHeight || image.height,
          });
        }}
        style={{ width: "100%", height: "auto", display: "block", borderRadius: "8px" }}
      />
      {dimensions.width > 0 && (
        <svg
          viewBox={`0 0 ${sourceWidth} ${sourceHeight}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
        >
          {boxes.map((box, index) => {
            const normalized = box.every((value) => value >= 0 && value <= 1);
            return (
              <rect
                key={`box-${index}`}
                x={normalized ? box[0] * sourceWidth : box[0]}
                y={normalized ? box[1] * sourceHeight : box[1]}
                width={normalized ? box[2] * sourceWidth : box[2]}
                height={normalized ? box[3] * sourceHeight : box[3]}
                fill="rgba(255, 56, 56, 0.16)"
                stroke="#ff3838"
                strokeWidth={Math.max(2, sourceWidth * 0.004)}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
          {segments.map((segment, index) => {
            const normalized = segment.every(
              ([x, y]) => x >= 0 && x <= 1 && y >= 0 && y <= 1
            );
            const points = segment
              .map(([x, y]) =>
                `${normalized ? x * sourceWidth : x},${normalized ? y * sourceHeight : y}`
              )
              .join(" ");

            return (
              <polygon
                key={`segment-${index}`}
                points={points}
                fill="rgba(55, 85, 211, 0.3)"
                stroke="#3755d3"
                strokeWidth={Math.max(2, sourceWidth * 0.004)}
                vectorEffect="non-scaling-stroke"
              />
            );
          })}
        </svg>
      )}
    </div>
  );
});

const normalizeStreamPacket = (packet) => {
  const raw = String(packet || "").trim();
  if (!raw) return null;
  if (raw === STREAM_STATUS.DONE || raw === `"${STREAM_STATUS.DONE}"`) {
    return STREAM_STATUS.DONE;
  }

  const withoutPrefix = raw.startsWith("data:")
    ? raw.replace(/^data:\s*/, "")
    : raw.startsWith("message:")
      ? raw.replace(/^message:\s*/, "")
      : raw;

  const first = safeJsonParse(withoutPrefix);
  if (!first.ok) return withoutPrefix;

  if (typeof first.value === "string") {
    const second = safeJsonParse(first.value);
    return second.ok ? second.value : first.value;
  }

  return first.value;
};

const closeRegisteredStream = (evalId) => {
  const existing = ACTIVE_STREAMS_BY_EVAL_ID.get(evalId);
  if (!existing) return;

  existing.retryTimer && clearTimeout(existing.retryTimer);
  existing.typingTimer && clearInterval(existing.typingTimer);
  existing.eventSource?.close?.();
  ACTIVE_STREAMS_BY_EVAL_ID.delete(evalId);
};

export const closeLlmEvaluationStream = (evalId) => {
  closeRegisteredStream(evalId);
};

const registerStreamHandle = (evalId, patch) => {
  if (!evalId) return;
  const existing = ACTIVE_STREAMS_BY_EVAL_ID.get(evalId) || {};
  ACTIVE_STREAMS_BY_EVAL_ID.set(evalId, { ...existing, ...patch });
};

const DeepEdgeAnalysingIndicator = ({ message = "Analysing..." }) => (
  <div className="d-a gap-2">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="30"
      height="30"
      viewBox="0 0 31 28"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M23 3h-9C7.925 3 3 7.925 3 14v0c0 6.075 4.925 11 11 11h9M21 14h7"
        stroke="#3755d3"
        className="path"
        strokeWidth="6"
        strokeLinecap="round"
      />
    </svg>
    <p className="animated-text m-0 p-0">{message}</p>
  </div>
);

const NoResponseIndicator = () => (
  <div className="d-a gap-2">
    <div />
    <p className="animated-text m-0 p-0 fw-bold">{NO_RESPONSE_MESSAGE}</p>
  </div>
);


export default function LlmEvaluationField({
  data,
  eval_id,
  sessionKey,
  chatBox,
  onEvaluationCreated,
  message,
  chatBoxPrompt,
  setChatBoxPrompt,
  chatBoxPromptid,
  modelName,
  chatImag,
  session,
  updateSession,
  chipset,
  compression,
  latestDevice,
  loadButton,
  setLoadButton,
  deviceStatusMap = {},
  evaluationStatusMap,
  busyPromptByEvalId,
  setBusyPromptByEvalId,
  selectedDeviceInfo,
  tokensRemaining,
  useGlobalScroll = false,
  toolbarActions = null,
}) {

  const activeSessionId = sessionKey || eval_id || LLM_DRAFT_SESSION_ID;
  const currentStatus = evaluationStatusMap?.status || message || "";

  const applySessionUpdate = useCallback(
    (updates, targetEvalId = activeSessionId) => {
      if (!targetEvalId || typeof updateSession !== "function") return;
      updateSession(updates, targetEvalId);
    },
    [activeSessionId, updateSession]
  );

  const evaluationModelName = String(data?.info?.name || modelName || "").trim();
  const normalizedModelName = evaluationModelName.toLowerCase();
  const singleRunTaskType = ["longclip", "xclip", "tinyclip"].includes(normalizedModelName)
    ? TASK_TYPES.ZERO_SHOT_CLASSIFICATION
    : normalizedModelName === "owlvit"
      ? TASK_TYPES.ZERO_SHOT_DETECTION
      : "";
  const taskType = singleRunTaskType || normalizeTaskType(
    data?.info?.task_type || MODEL_TASK_TYPE_BY_NAME[modelName] || ""
  );
  const isVisualQuestionAnswering = taskType === TASK_TYPES.VISUAL_QUESTION_ANSWERING;
  const isZeroShotClassification = taskType === TASK_TYPES.ZERO_SHOT_CLASSIFICATION;
  const isTextGeneration = taskType === TASK_TYPES.TEXT_GENERATION;
  const isZeroShotDetection = taskType === TASK_TYPES.ZERO_SHOT_DETECTION;
  const isZeroShotTask = isZeroShotClassification || isZeroShotDetection;
  const supportsTextOnlyVisionInput = [
    "internvl-3.5-1b",
    "llava-onevision-7b",
    "llava-onevision-qwen2-7b",
  ].includes(normalizedModelName);
  const supportsImage = isVisualQuestionAnswering || isZeroShotTask;
  const isFlorence = normalizedModelName.startsWith("florence");
  const hubSampleModelName = evaluationModelName;
  const hubSampleEndpoint = String(
    data?.cloudfront_details?.aws_cf_endpoint || ""
  ).trim().replace(/\/+$/, "");
  const hubSampleAccessKey = data?.cloudfront_details?.aws_cf_access_key;
  const hubSampleSecretKey = data?.cloudfront_details?.aws_cf_secret_key;
  const hubSampleBucketName = data?.cloudfront_details?.aws_cf_bucket_name;
  const [evaluationExamples, setEvaluationExamples] = useState([]);
  const [activeExampleModality, setActiveExampleModality] = useState("text");
  const [isExampleGalleryHovered, setIsExampleGalleryHovered] = useState(false);
  const [examplePhase, setExamplePhase] = useState("enter");
  const activeExampleModalityRef = useRef("text");
  const exampleSlideDirectionRef = useRef(1);
  const exampleGalleryRowRef = useRef(null);
  const shouldDecodeEscapedUnicode = [
    "llava-onevision-7b",
    "deepseek-r1-distill-qwen-7b",
    "internvl-3.5-1b",
  ].includes(normalizedModelName);

  const [selectedFamily, setSelectedFamily] = useState('ambarella');
  const [selectedChipset, setSelectedChipset] = useState(chipset || null);
  const [selectedCompression, setSelectedCompression] = useState(compression || null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [promptText, setPromptText] = useState("");
  const promptTextRef = useRef("");
  const [promptDuplicate, setPromptDuplicate] = useState('')
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [historyError, setHistoryError] = useState(false);
  const isEvaluationReentryRef = useRef(
    Boolean(
      chatBox &&
      eval_id &&
      eval_id !== chatBoxPromptid &&
      String(currentStatus).trim().toLowerCase() === "evaluating"
    )
  );
  const [reentryHistoryHydrating, setReentryHistoryHydrating] = useState(
    () => isEvaluationReentryRef.current
  );
  const [longclipClassNames, setLongclipClassNames] = useState([]);
  const [longclipClassDraft, setLongclipClassDraft] = useState("");
  const longclipClassInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [resettingContext, setResettingContext] = useState(false);
  const [contextPanelOpen, setContextPanelOpen] = useState(
    () => typeof window === "undefined" || window.innerWidth >= 992
  );
  const [isCreatingContext, setIsCreatingContext] = useState(false);
  const contextCreationInFlightRef = useRef(false);
  const [, setStoppingEvaluation] = useState(false);
  const stopRequestInFlightRef = useRef(false);
  const [selectingSuggestedImage, setSelectingSuggestedImage] = useState("");
  const [activeExampleId, setActiveExampleId] = useState("");
  const activeExampleIdRef = useRef("");
  const [hoveredTextExampleId, setHoveredTextExampleId] = useState("");
  const textPreviewExampleIdRef = useRef("");
  const promptBeforeTextPreviewRef = useRef("");
  const suggestedImageSelectionInFlightRef = useRef(false);
  const replaceImagesOnSelectRef = useRef(false);
  const exampleImageCacheRef = useRef(new Map());

  const downloadHubSampleAsset = useCallback(async (assetUrl) => {
    try {
      const response = await fetch(assetUrl);
      if (!response.ok) {
        throw new Error(`Hub sample request failed with status ${response.status}.`);
      }

      return await response.blob();
    } catch (cloudFrontError) {
      if (!hubSampleAccessKey || !hubSampleSecretKey || !hubSampleBucketName) {
        throw cloudFrontError;
      }

      const assetPath = new URL(assetUrl).pathname.replace(/^\/+/, "");
      const s3Client = new S3Client({
        region: REGION,
        credentials: {
          accessKeyId: hubSampleAccessKey,
          secretAccessKey: hubSampleSecretKey,
        },
      });
      const response = await s3Client.send(
        new GetObjectCommand({
          Bucket: hubSampleBucketName,
          Key: decodeURIComponent(assetPath),
        })
      );
      const bytes = await response.Body.transformToByteArray();

      return new Blob([bytes], {
        type: response.ContentType || "application/octet-stream",
      });
    }
  }, [
    hubSampleAccessKey,
    hubSampleBucketName,
    hubSampleSecretKey,
  ]);

  useEffect(() => {
    let cancelled = false;

    setEvaluationExamples([]);
    setActiveExampleModality("text");
    exampleImageCacheRef.current.clear();

    if (!hubSampleEndpoint || !hubSampleModelName) {
      return () => {
        cancelled = true;
      };
    }

    const samplesUrl = `${hubSampleEndpoint}/sample_examples/${hubSampleModelName}/${hubSampleModelName}.json`;

    getCachedHubSamples(samplesUrl, hubSampleModelName, downloadHubSampleAsset)
      .then((samples) => {
        if (!cancelled) {
          setEvaluationExamples(samples);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          console.error("Failed to load hub sample examples", error);
          setEvaluationExamples([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    downloadHubSampleAsset,
    hubSampleEndpoint,
    hubSampleModelName,
  ]);

  useEffect(() => {
    const availableModalities = EXAMPLE_MODALITY_OPTIONS
      .map((option) => option.value)
      .filter((modality) =>
        evaluationExamples.some((example) => example.modality === modality)
      );

    if (!availableModalities.length) return;

    setActiveExampleModality((current) => {
      const next = availableModalities.includes(current)
        ? current
        : availableModalities[0];
      activeExampleModalityRef.current = next;
      return next;
    });
    setExamplePhase("enter");
  }, [evaluationExamples]);

  const availableExampleModalities = useMemo(
    () => EXAMPLE_MODALITY_OPTIONS
      .map((option) => option.value)
      .filter((modality) =>
        evaluationExamples.some((example) => example.modality === modality)
      ),
    [evaluationExamples]
  );

  useEffect(() => {
    activeExampleModalityRef.current = activeExampleModality;
    setExamplePhase("enter");
  }, [activeExampleModality]);

  useEffect(() => {
    promptTextRef.current = promptText;
  }, [promptText]);

  useEffect(() => {
    if (
      availableExampleModalities.length <= 1 ||
      isExampleGalleryHovered ||
      examplePhase !== "enter"
    ) {
      return undefined;
    }

    const holdTimer = window.setTimeout(() => {
      setExamplePhase("exit");
    }, 4800);

    return () => window.clearTimeout(holdTimer);
  }, [
    activeExampleModality,
    availableExampleModalities.length,
    examplePhase,
    isExampleGalleryHovered,
  ]);

  useEffect(() => {
    if (examplePhase !== "exit" || availableExampleModalities.length <= 1) {
      return undefined;
    }

    const swapTimer = window.setTimeout(() => {
      const current = activeExampleModalityRef.current;
      const currentIndex = availableExampleModalities.indexOf(current);
      if (currentIndex <= 0) exampleSlideDirectionRef.current = 1;
      if (currentIndex >= availableExampleModalities.length - 1) {
        exampleSlideDirectionRef.current = -1;
      }
      const nextIndex =
        currentIndex < 0 ? 0 : currentIndex + exampleSlideDirectionRef.current;
      const next = availableExampleModalities[nextIndex] || availableExampleModalities[0];
      activeExampleModalityRef.current = next;
      setActiveExampleModality(next);
      setExamplePhase("enter");
    }, 360);

    return () => window.clearTimeout(swapTimer);
  }, [availableExampleModalities, examplePhase]);

  useEffect(() => {
    setActiveExampleId("");
    activeExampleIdRef.current = "";
  }, [modelName, taskType]);

  const [isDragging, setIsDragging] = useState(false);
  const [Creating, setCreating] = useState(false);
  const [errors, setErrors] = useState({
    family: false,
    chipset: false,
    prompt: false
  });
  const scrollRef = useRef(null);
  const scrollHostRef = useRef(null);
  const fileInputRef = useRef(null);
  const autoScrollRef = useRef(true);
  const lastScrollTopRef = useRef(0);
  const topPagingSuppressedUntilRef = useRef(0);
  const pagingRequestInFlightRef = useRef(false);
  const historyRequestedContextsRef = useRef(new Set());
  const BOTTOM_THRESHOLD = 80; 
  const familyRef = useRef(null);
  const chipsetRef = useRef(null);
  const uploadRef = useRef(null);
  const promptRef = useRef(null);
  const hardwareRef = useRef(null);
  const promptTextareaRef = useRef(null);
  const startBtnRef = useRef(null);
  const startInFlightRef = useRef(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [modalFlorenceSpatial, setModalFlorenceSpatial] = useState(null);
  const [boundingboxes, setboundingboxes] = useState([]);
  const [owlvitscore, setowlvitscore] = useState([]);
  const createSafeThumbFromBase64 = async (base64, maxSize = 256) => {
    const img = new Image();
    img.src = base64;

    await new Promise(res => (img.onload = res));

    const scale = Math.min(
      maxSize / img.width,
      maxSize / img.height,
      1
    );

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);

    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    return new Promise(resolve => {
      canvas.toBlob(
        blob => resolve(blob ? URL.createObjectURL(blob) : ""),
        "image/jpeg",
        0.6
      );
    });
  };

  const createVideoThumbnail = (videoUrl) => {
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.src = videoUrl;
      video.crossOrigin = "anonymous";
      video.muted = true;
      video.playsInline = true;

      video.onloadeddata = () => {
        video.currentTime = Math.min(0.1, video.duration || 0);
      };

      video.onseeked = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 180;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve("");
              return;
            }
            resolve(URL.createObjectURL(blob));
          },
          "image/jpeg",
          0.7
        );
      };

      video.onerror = () => {
        resolve("");
      };
    });
  };






  const openImage = useCallback((src, boxes, score) => {
    setModalImage(src);
    setboundingboxes(boxes);
    setowlvitscore(score);
    setModalFlorenceSpatial(null);
    setShowImageModal(true);
  }, []);

  const openFlorenceImage = useCallback((src, spatial) => {
    setModalImage(src);
    setModalFlorenceSpatial(spatial);
    setboundingboxes([]);
    setowlvitscore([]);
    setShowImageModal(true);
  }, []);






  const [guideActive, setGuideActive] = useState(false);
  const infoPopoverTimeoutRef = useRef(null);

  const [infoPopover, setInfoPopover] = useState({
    open: false,
    target: null,
    message: "",
  });

  const highlightSection = (ref) => {
    if (!ref?.current) return;

    ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    ref.current.classList.add("highlight-required");

    setTimeout(() => {
      ref.current?.classList.remove("highlight-required");
    }, 3500);
  };

  const showInfoPopover = (targetId, message, timeout = 2500) => {
    if (infoPopoverTimeoutRef.current) {
      clearTimeout(infoPopoverTimeoutRef.current);
      infoPopoverTimeoutRef.current = null;
    }

    setInfoPopover({ open: true, target: targetId, message });

    infoPopoverTimeoutRef.current = setTimeout(() => {
      setInfoPopover({ open: false, target: null, message: "" });
      infoPopoverTimeoutRef.current = null;
    }, timeout);
  };

  const hideInfoPopover = () => {
    if (infoPopoverTimeoutRef.current) {
      clearTimeout(infoPopoverTimeoutRef.current);
      infoPopoverTimeoutRef.current = null;
    }
    setInfoPopover({ open: false, target: null, message: "" });
  };




  useEffect(() => {
    setSelectedChipset(chipset || null);
  }, [chipset]);

  const compressionOptions = useMemo(() => {
    if (!Array.isArray(data?.compression)) return [];

    return data.compression
      .filter((item) => item !== null && item !== undefined && item !== "")
      .map((item) => ({
        value: item,
        label: String(item).replace(/_/g, " "),
      }));
  }, [data?.compression]);

  useEffect(() => {
    setSelectedCompression((current) => {
      const urlCompression = compressionOptions.find(
        (option) =>
          compression &&
          String(option.value).toLowerCase() === String(compression).toLowerCase()
      );

      if (urlCompression) return urlCompression.value;

      return compressionOptions.some((option) => option.value === current)
        ? current
        : null;
    });
  }, [compression, compressionOptions]);

  useEffect(() => {
    if (!eval_id) return;

    const currentStatus = evaluationStatusMap?.status || message;
    if (!currentStatus) return;

    if (currentStatus === "Queued") {
      applySessionUpdate((prev) => {
        const pending = prev.pendingPredictionsByEvalId?.[eval_id];
        if (!pending || pending.queuedSeen) return prev;

        return {
          ...prev,
          pendingPredictionsByEvalId: {
            ...(prev.pendingPredictionsByEvalId || {}),
            [eval_id]: {
              ...pending,
              queuedSeen: true,
              queueRetryStarted: false,
            },
          },
        };
      });
      return;
    }

    if (currentStatus !== "Evaluating") return;

    const pending = session.pendingPredictionsByEvalId?.[eval_id];
    if (!pending?.queuedSeen || pending?.queueRetryStarted) return;

    applySessionUpdate((prev) => {
      const currentPending = prev.pendingPredictionsByEvalId?.[eval_id];
      if (!currentPending || currentPending.queueRetryStarted) return prev;

      return {
        ...prev,
        pendingPredictionsByEvalId: {
          ...(prev.pendingPredictionsByEvalId || {}),
          [eval_id]: {
            ...currentPending,
            queueRetryStarted: true,
          },
        },
      };
    });

    handlepredictions(
      pending.evalId || eval_id,
      pending.prompt || "",
      pending.chatImage || null,
      pending.imageName || "none",
      pending.chatImages || [],
      "retry",
      pending.turnId || null
    );
  }, [eval_id, evaluationStatusMap?.status, message, session.pendingPredictionsByEvalId]);




  useEffect(() => {
    setSelectedDevice(latestDevice || "");
  }, [latestDevice]);

  useEffect(() => {
    if (!eval_id) return;

    autoScrollRef.current = false;
    lastScrollTopRef.current = 0;
    topPagingSuppressedUntilRef.current = 0;
    pagingRequestInFlightRef.current = false;

    applySessionUpdate(prev => ({
      ...prev,
      selectedImages: []
    }));
  }, [eval_id]);



  useEffect(() => {
    if (!evaluationStatusMap) return;

    const statusEntries = evaluationStatusMap?.evalId
      ? [[evaluationStatusMap.evalId, evaluationStatusMap.status]]
      : Object.entries(evaluationStatusMap);

    statusEntries.forEach(([id, status]) => {
      if (status !== "Evaluating") {
        setBusyPromptByEvalId(prev => {
          if (!prev?.[id]) return prev;
          return { ...prev, [id]: false };
        });
      }
    });
  }, [evaluationStatusMap]);


  useEffect(() => {
    let dragCounter = 0;

    const handleDragEnter = (e) => {
      e.preventDefault();
      dragCounter++;
      setIsDragging(true);
    };

    const handleDragOver = (e) => {
      e.preventDefault();
    };

    const handleDragLeave = (e) => {
      dragCounter--;
      if (dragCounter === 0) setIsDragging(false);
    };

    const handleDrop = (e) => {
      e.preventDefault();
      dragCounter = 0;
      setIsDragging(false);

      const files = e.dataTransfer.files;

      if (files.length > 0) {
        processFiles(files);
      }
    };

    window.addEventListener("dragenter", handleDragEnter);
    window.addEventListener("dragover", handleDragOver);
    window.addEventListener("dragleave", handleDragLeave);
    window.addEventListener("drop", handleDrop);

    return () => {
      window.removeEventListener("dragenter", handleDragEnter);
      window.removeEventListener("dragover", handleDragOver);
      window.removeEventListener("dragleave", handleDragLeave);
      window.removeEventListener("drop", handleDrop);
    };
  }, [isFlorence, isVisualQuestionAnswering, session?.selectedImages?.length]);

  const {
    history = [],
    selectedImages = [],
    chatImages = [],
    pendingPrompt = "",
    loading = false,
    firstEvaluation = true,
    totalPages = 1,
    page = 1,
    sse = null,
    responseStreaming = false,
    streamWait = false,
    activeContext = 1,
    availableContexts = [1],
    contextChatSummaries = {},
    streamingContext = 1,
  } = session;

  useEffect(() => {
    const last = history[history.length - 1];

    const shouldBeBusy =
      responseStreaming && last?.response !== STREAM_STATUS.ANALYSING;

    setBusy(shouldBeBusy);
  }, [responseStreaming, history, eval_id]);

  useEffect(() => {
    if (!eval_id) return;


    if (!responseStreaming) {
      setEvalBusyPrompt(eval_id, false);
    }
  }, [responseStreaming, eval_id]);

  const setSessionStreamWait = (value, targetEvalId = activeSessionId) =>
    applySessionUpdate(prev => ({ ...prev, streamWait: value }), targetEvalId);



  const [loadedImages, setLoadedImages] = useState({});
  const loadedImagesRef = useRef({});
  const loadedImagesFrameRef = useRef(null);
  const handleImageLoad = useCallback((key) => {
    if (!key || loadedImagesRef.current[key]) return;

    loadedImagesRef.current[key] = true;

    if (loadedImagesFrameRef.current) return;

    const schedule =
      typeof window !== "undefined" && window.requestAnimationFrame
        ? window.requestAnimationFrame
        : (callback) => setTimeout(callback, 16);

    loadedImagesFrameRef.current = schedule(() => {
      loadedImagesFrameRef.current = null;
      setLoadedImages({ ...loadedImagesRef.current });
    });
  }, []);

  useEffect(() => {
    return () => {
      if (!loadedImagesFrameRef.current) return;

      if (typeof window !== "undefined" && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(loadedImagesFrameRef.current);
      } else {
        clearTimeout(loadedImagesFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadedImagesRef.current = {};
    setLoadedImages({});
  }, [eval_id]);

  const evaluationCompleted = message === "Complete" || message === "Terminated" || isZeroShotTask;
  const supportsContextTabs = chatBox && !isZeroShotTask && !isFlorence;
  const normalizedCurrentStatus = String(currentStatus).trim().toLowerCase();
  const isFinishedEvaluation =
    normalizedCurrentStatus === "complete" ||
    normalizedCurrentStatus === "terminated";
  const isEvaluating = normalizedCurrentStatus === "evaluating";
  const normalizedContexts = normalizeAvailableContexts(
    availableContexts,
    activeContext
  );
  const getContextTitle = (contextNumber) => {
    const summary = contextChatSummaries?.[contextNumber];
    const normalizedSummary = typeof summary === "string" ? summary.trim() : "";

    return normalizedSummary;
  };
  const latestContext = normalizedContexts[normalizedContexts.length - 1] || 1;
  const showContextNavigation =
    supportsContextTabs && (isEvaluating || isFinishedEvaluation);
  const isLatestContext = activeContext === latestContext;
  const latestContextHistory =
    activeContext === latestContext
      ? history
      : session.contextHistories?.[latestContext];
  const latestContextHasHistory =
    Array.isArray(latestContextHistory) && latestContextHistory.length > 0;
  const showContextExamples =
    chatBox &&
    supportsContextTabs &&
    isEvaluating &&
    isLatestContext &&
    !loadingHistory &&
    !reentryHistoryHydrating &&
    !streamWait &&
    history.length === 0;
  const activeContextStreamWait =
    streamWait &&
    (!supportsContextTabs || streamingContext === activeContext);
  const showReentryGenerationProgress =
    isEvaluationReentryRef.current && activeContextStreamWait;

  useEffect(() => {
    stopRequestInFlightRef.current = false;
    setStoppingEvaluation(false);
  }, [eval_id]);

  useEffect(() => {
    if (isEvaluating) return;

    stopRequestInFlightRef.current = false;
    setStoppingEvaluation(false);
  }, [isEvaluating]);


  /* ------------------------------------------------------------------
   * HELPERS FOR UPDATING SESSION
   * ------------------------------------------------------------------ */

  const contextMetadataRequestsRef = useRef(new Map());

  const refreshContextMetadata = useCallback(async (
    targetEvalId = eval_id,
    requestedContext = activeContext
  ) => {
    if (!targetEvalId || !supportsContextTabs) return false;

    const normalizedContext = normalizeContextNumber(requestedContext) || 1;
    const requestKey = `${targetEvalId}:${normalizedContext}`;
    const pendingRequest = contextMetadataRequestsRef.current.get(requestKey);

    if (pendingRequest) return pendingRequest;

    const filters = encodeURIComponent(JSON.stringify({
      _id: targetEvalId,
      context: normalizedContext,
    }));
    const url = `${window.ENV.API_URL}/evaluations?filters=${filters}&section=session_history&page=1&page_size=1&is_stream=false&visual_inspection=false`;
    const request = getDataFromApi(url, "GET")
      .then((payload) => {
        const {
          contextChatSummaries: responseContextSummaries,
          availableContexts: responseContexts,
        } = getContextMetadata(payload, targetEvalId);

        if (!Object.keys(responseContextSummaries).length) return false;

        applySessionUpdate(prev => ({
          ...prev,
          availableContexts: normalizeAvailableContexts(
            [
              ...(prev.availableContexts || []),
              ...responseContexts,
              normalizedContext,
            ],
            normalizedContext
          ),
          contextChatSummaries: {
            ...(prev.contextChatSummaries || {}),
            ...responseContextSummaries,
          },
        }), targetEvalId);

        return true;
      })
      .catch((error) => {
        console.error("Failed to refresh context summaries", error);
        return false;
      })
      .finally(() => {
        contextMetadataRequestsRef.current.delete(requestKey);
      });

    contextMetadataRequestsRef.current.set(requestKey, request);
    return request;
  }, [activeContext, applySessionUpdate, eval_id, supportsContextTabs]);
  const activeContextTitle = getContextTitle(activeContext);

  useEffect(() => {
    if (
      !showContextNavigation ||
      !eval_id ||
      activeContextTitle
    ) return;

    refreshContextMetadata(eval_id, activeContext);
  }, [
    activeContext,
    eval_id,
    normalizedCurrentStatus,
    activeContextTitle,
    refreshContextMetadata,
    showContextNavigation,
  ]);
  const update = (obj, targetEvalId = activeSessionId) => applySessionUpdate(obj, targetEvalId);

  useEffect(() => {
    if (!supportsContextTabs || !eval_id) return;

    applySessionUpdate(prev => ({
      ...prev,
      contextHistories: {
        ...(prev.contextHistories || {}),
        [prev.activeContext || 1]: Array.isArray(prev.history) ? prev.history : [],
      },
    }), eval_id);
  }, [activeContext, applySessionUpdate, eval_id, history, supportsContextTabs]);

  /* ------------------------------------------------------------------
   * FILE HANDLING
   * ------------------------------------------------------------------ */

  const handleFileSelect = (options = {}) => {
    if (!supportsImage) return;

    const replace = options?.replace === true;
    const maxItems = isVisualQuestionAnswering
      ? (isFlorence ? 1 : MAX_VQA_IMAGES)
      : Number.POSITIVE_INFINITY;

    if (!replace && selectedImages.length >= maxItems) {
      showInfoPopover(
        "llm-upload-step",
        isFlorence
          ? "Florence2 supports one image at a time."
          : `You can select up to ${MAX_VQA_IMAGES} images.`,
        3000
      );
      return;
    }

    replaceImagesOnSelectRef.current = replace;

    if (fileInputRef.current) {
      fileInputRef.current.value = "";  
      fileInputRef.current.accept = "image/*,.zip";
      fileInputRef.current.multiple = !isFlorence;
      fileInputRef.current.click();
    }
  };

  const sanitizeImageName = (name, fallbackName = "image") => {
    const rawName = String(name || fallbackName || "image")
      .split(/[\\/]/)
      .pop();
    const sanitized = rawName
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_.-]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^\.+/, "")
      .replace(/\.+$/, "");

    return sanitized || fallbackName || "image";
  };

  const getImageNameKey = (name) => String(name || "").toLowerCase();

  const getReservedImageNameSet = (images = []) =>
    new Set(
      images
        .map((img) => img?.name)
        .filter(Boolean)
        .map((name) => getImageNameKey(sanitizeImageName(name)))
    );

  const makeUniqueImageName = (name, existing, fallbackName = "image") => {
    const safeName = sanitizeImageName(name, fallbackName);
    const dotIndex = safeName.lastIndexOf(".");
    const hasExtension = dotIndex > 0 && dotIndex < safeName.length - 1;
    const base = hasExtension ? safeName.slice(0, dotIndex) : safeName;
    const ext = hasExtension ? safeName.slice(dotIndex) : "";
    let counter = 1;
    let uniqueName = safeName;

    while (existing.has(getImageNameKey(uniqueName))) {
      uniqueName = `${base}_${counter}${ext}`;
      counter += 1;
    }

    return uniqueName;
  };

  const renameFile = (file, safeName) => {
    if (!file || file.name === safeName) return file;

    return new File([file], safeName, {
      type: file.type,
      lastModified: file.lastModified,
    });
  };

  const normalizeMediaItems = (items = [], existing = new Set()) =>
    items.map((item, index) => {
      const safeName = makeUniqueImageName(
        item?.name || `image_${index + 1}`,
        existing,
        `image_${index + 1}`
      );
      existing.add(getImageNameKey(safeName));

      return {
        ...item,
        name: safeName,
        file: item?.file ? renameFile(item.file, safeName) : item?.file,
      };
    });

  const processFiles = async (files, options = {}) => {
    if (!files || files.length === 0) return;

    const replace = options.replace === true;
    replaceImagesOnSelectRef.current = false;

    const fileArray = Array.from(files);
    const existing = replace ? new Set() : getReservedImageNameSet(selectedImages);
    const maxItems = isVisualQuestionAnswering
      ? (isFlorence ? 1 : MAX_VQA_IMAGES)
      : Number.POSITIVE_INFINITY;
    const currentCount = replace ? 0 : selectedImages.length;
    let remainingSlots = Math.max(0, maxItems - currentCount);
    let selectionWasLimited = remainingSlots === 0;
    const nextImages = [];

    for (const file of fileArray) {
      if (remainingSlots <= 0) {
        selectionWasLimited = true;
        break;
      }

      if (file.type.startsWith("image/")) {
        const uniqueName = makeUniqueImageName(file.name, existing);
        existing.add(getImageNameKey(uniqueName));
        const renamed = renameFile(file, uniqueName);
        if (replace) {
          nextImages.push(await createImagePreviewItem(renamed));
        } else {
          await previewImage(renamed);
        }
        remainingSlots -= 1;
      } else if (file.type === "application/zip") {
        if (replace) {
          update({ selectedImages: [] });
        }
        const result = await extractZip(file, existing, remainingSlots);
        remainingSlots -= result.added;
        selectionWasLimited = selectionWasLimited || result.truncated;
      }
      else if (file.type?.startsWith("video/")) {
        showInfoPopover(
          "llm-upload-step",
          "Video input is not supported right now.",
          3000
        );
      }
    }

    if (replace && nextImages.length > 0) {
      update({ selectedImages: nextImages });
    }

    if (
      isVisualQuestionAnswering &&
      (selectionWasLimited || fileArray.length > Math.max(0, maxItems - currentCount))
    ) {
      showInfoPopover(
        "llm-upload-step",
        isFlorence
          ? "Florence2 supports one image at a time."
          : `You can select up to ${MAX_VQA_IMAGES} images.`,
        3000
      );
    }
  };

const MAX_DIMENSION = 2048;

const resizeImageIfNeeded = (file) => {
  return new Promise((resolve) => {
    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target.result;
    };

    img.onload = () => {
      const { width, height } = img;

      
      if (width <= MAX_DIMENSION && height <= MAX_DIMENSION) {
        resolve(file);
        return;
      }

      const scale = Math.min(
        MAX_DIMENSION / width,
        MAX_DIMENSION / height
      );

      const canvas = document.createElement("canvas");
      canvas.width = Math.round(width * scale);
      canvas.height = Math.round(height * scale);

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        resolve(
          new File([blob], file.name, {
            type: file.type,
          })
        );
      }, file.type, 0.9);
    };

    reader.readAsDataURL(file);
  });
};

  const createImagePreviewItem = async (file) => {
    file = await resizeImageIfNeeded(file);

    const base64 = await new Promise(res => {
      const r = new FileReader();
      r.onload = e => res(e.target.result);
      r.readAsDataURL(file);
    });

    const thumbUrl = await createSafeThumbFromBase64(base64);

    return {
      name: file.name,
      type: "image",
      base64,
      thumbUrl
    };
  };

  const previewImage = async (file) => {
    const item = await createImagePreviewItem(file);

    update(prev => ({
      selectedImages: [
        ...(prev.selectedImages || []),
        item
      ]
    }));

    return item;
  };

  const previewVideo = async (file) => {
    const videoUrl = URL.createObjectURL(file);
    const thumbUrl = await createVideoThumbnail(videoUrl);

    update((prev) => ({
      selectedImages: [
        ...(prev.selectedImages || []),
        {
          name: file.name,
          file,
          type: "video",
          url: videoUrl,
          thumbUrl,
        },
      ],
    }));
  };

  // ---------------- TOKEN GUARD ----------------
  const IMAGE_TOKEN_COST = 256;

  const estimatePromptTokens = (text) => {
    const s = (text || "").trim();
    if (!s) return 0;

   
    const tokens = s.match(/[A-Za-z0-9_]+(?:'[A-Za-z0-9_]+)*|[^\sA-Za-z0-9_]/g);
    return tokens ? tokens.length : 0;
  };


  const safeNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const tokensRemainingNum = safeNum(tokensRemaining);
  const hasTokenLimit = tokensRemainingNum !== null;

  // live estimated tokens
  const promptTokens = useMemo(() => estimatePromptTokens(promptText), [promptText]);
  const imageCount = (session?.selectedImages?.length || 0);
  const imageTokens = imageCount * IMAGE_TOKEN_COST;
  const estimatedTotalTokens = promptTokens + imageTokens;

  const tokensExceeded =
    hasTokenLimit && estimatedTotalTokens > tokensRemainingNum;

  const tokensShortMsg = hasTokenLimit
    ? `Token limit exceeded. total ${estimatedTotalTokens} tokens (prompt ~${promptTokens} + images ${imageTokens}), but only ${tokensRemainingNum} left.`
    : "";

  const getTokenLimitError = (promptValue = promptText, media = selectedImages) => {
    if (!hasTokenLimit) return "";

    const submittedPromptTokens = estimatePromptTokens(promptValue);
    const submittedImageTokens = normalizeMediaItems(media).length * IMAGE_TOKEN_COST;
    const submittedTotalTokens = submittedPromptTokens + submittedImageTokens;

    return submittedTotalTokens > tokensRemainingNum
      ? `Token limit exceeded. total ${submittedTotalTokens} tokens (prompt ~${submittedPromptTokens} + images ${submittedImageTokens}), but only ${tokensRemainingNum} left.`
      : "";
  };


  const getLongclipPromptError = (text) => {
    
    if (!isZeroShotClassification) return "";

    const value = (text || "").trim();
    if (!value) return "";

    
    const parts = value.split(",").map((p) => p.trim());

    
    if (value.endsWith(",")) {
      return "Remove the comma at the end or add one more prompt.";
    }

    
    if (parts.some((p) => !p)) {
      return "Enter a valid comma separated prompt.";
    }



    return "";
  };
  // ---------------------------------------------


  /* ------------------------------------------------------------------
   * ZIP Extract
   * ------------------------------------------------------------------ */
  const extractZip = async (
    zipFile,
    existing,
    maxItems = Number.POSITIVE_INFINITY
  ) => {
    const buf = await zipFile.arrayBuffer();
    const unzipped = unzipSync(new Uint8Array(buf));
    let added = 0;
    let truncated = false;

    if (Object.keys(unzipped).some((fname) => /\.(mp4|avi|mov|webm)$/i.test(fname))) {
      showInfoPopover(
        "llm-upload-step",
        "Video input is not supported right now. Remove videos from the ZIP and try again.",
        3000
      );
      return { added, truncated };
    }

    for (const [fname, uint8] of Object.entries(unzipped)) {
      if (/\.(png|jpe?g|gif|bmp|webp)$/i.test(fname)) {
        if (added >= maxItems) {
          truncated = true;
          break;
        }

        const mime = getMimeType(fname);
        const blob = new Blob([uint8], { type: mime });
        const base = fname.split("/").pop();
        const unique = makeUniqueImageName(base, existing);
        existing.add(getImageNameKey(unique));

        await previewImage(new File([blob], unique, { type: mime }));
        added += 1;
      }
    }

    return { added, truncated };
  };

  const getMimeType = (fname) => {
    const ext = fname.toLowerCase().split(".").pop();
    return (
      {
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        bmp: "image/bmp",
        gif: "image/gif",
        webp: "image/webp"
      }[ext] || "application/octet-stream"
    );
  };

  /* ------------------------------------------------------------------
   * REMOVE IMAGE
   * ------------------------------------------------------------------ */
  const removeImage = (idx) => {
    update({
      selectedImages: selectedImages.filter((_, i) => i !== idx)
    });
  };

  const loadSuggestedImage = async (suggestedImage) => {
    const cached = exampleImageCacheRef.current.get(suggestedImage.name);
    if (cached) return cached;

    const blob = await downloadHubSampleAsset(suggestedImage.src);
    const file = new File([blob], suggestedImage.name, {
      type: blob.type || "image/jpeg",
    });
    const item = await createImagePreviewItem(file);
    exampleImageCacheRef.current.set(suggestedImage.name, item);

    return item;
  };

  const setActiveExample = (exampleId) => {
    activeExampleIdRef.current = exampleId;
    setActiveExampleId(exampleId);
  };

  /* ------------------------------------------------------------------
   * SCROLL
   * ------------------------------------------------------------------ */
  const getScrollElement = useCallback(() => {
    if (useGlobalScroll) {
      return scrollHostRef.current;
    }
    return scrollRef.current;
  }, [useGlobalScroll]);

  const scrollToBottom = useCallback((smooth = false, force = false) => {
    if (!force && !autoScrollRef.current) return;
    const el = getScrollElement();
    if (!el) return;

    el.scrollTo({
      top: el.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    });
  }, [getScrollElement]);

  const getPrependScrollAnchor = () => {
    const el = getScrollElement();
    if (!el) return null;

    return {
      scrollHeight: el.scrollHeight,
      scrollTop: el.scrollTop,
    };
  };

  const restorePrependScrollAnchor = (anchor) => {
    if (!anchor) return;

    const restore = () => {
      const el = getScrollElement();
      if (!el) return;

      el.scrollTop = anchor.scrollTop + (el.scrollHeight - anchor.scrollHeight);
    };

    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(() => window.requestAnimationFrame(restore));
      return;
    }

    setTimeout(restore, 0);
  };

  const scrollToTop = (suppressTopPaging = false, preserveAutoScroll = false) => {
    const el = getScrollElement();
    if (!el) return;

    if (!preserveAutoScroll) {
      autoScrollRef.current = false;
    }
    if (suppressTopPaging) {
      topPagingSuppressedUntilRef.current = Date.now() + 500;
    }

    el.scrollTo({
      top: 0,
      behavior: "auto",
    });
  };

  useEffect(() => {
    if (!eval_id) return undefined;

    // Session history opens at its newest entry. This also covers cached
    // histories, where no page-one request occurs to position the scroller.
    autoScrollRef.current = true;
    topPagingSuppressedUntilRef.current = Date.now() + 500;
    const frame = window.requestAnimationFrame(() => {
      scrollToBottom(false, true);
      const el = getScrollElement();
      lastScrollTopRef.current = el?.scrollTop || 0;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [eval_id, getScrollElement, scrollToBottom]);


  useEffect(() => {
    if (!autoScrollRef.current) return undefined;

    // Streaming state updates are committed after the SSE/typing callback.
    // Scroll on the next frame so scrollHeight includes the text that React
    // just rendered, including the final chunk of the response.
    const frame = window.requestAnimationFrame(() => {
      scrollToBottom();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [history, scrollToBottom]);

  useEffect(() => {
    if (
      !isZeroShotTask ||
      !autoScrollRef.current ||
      Object.keys(loadedImages).length === 0
    ) {
      return undefined;
    }

    // LongCLIP/OwlViT results continue growing after the response completes:
    // decoded previews replace their placeholders and prediction rows become
    // visible. Follow those post-stream layout changes as well.
    const frame = window.requestAnimationFrame(() => {
      scrollToBottom();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isZeroShotTask, loadedImages, scrollToBottom]);



  const getDeviceStatus = (device) => {
    return deviceStatusMap?.[device] || "Reserved";
  };

  const StatusDot = ({ status }) => {
    const isActive = status === "Active";
    const isInActive = status === "InActive";

    return (
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: isActive ? "#28a745" : isInActive ? "#dc3545" : "#f5ff00",
          display: "inline-block",
        }}
      />
    );
  };

  /* ------------------------------------------------------------------
   * SSE CLEANUP ON UNMOUNT
   * ------------------------------------------------------------------ */
  useEffect(() => {
    return () => {
      if (sse) sse.close();
    };
  }, []);



  function normalizeLLMResponse(text = "") {
    const normalized = String(text || "")
      .trim()
      .replace(/^"+|"+$/g, "")       
      .replace(/\\n/g, "\n")         
      .replace(/\\"/g, '"');         

    if (!shouldDecodeEscapedUnicode) return normalized;

    return normalized.replace(/\\u([0-9a-fA-F]{4})/g, (_, codePoint) =>
      String.fromCharCode(Number.parseInt(codePoint, 16))
    );
  }

  const buildMarkdownSegments = (rawText = "") => {
  const text = normalizeLLMResponse(rawText || "");
  const segments = [];
  let cursor = 0;

  while (cursor < text.length) {
    const fenceStart = text.indexOf("```", cursor);

    if (fenceStart === -1) {
      const content = text.slice(cursor);
      if (content.trim()) segments.push({ type: "text", content });
      break;
    }

    if (fenceStart > cursor) {
      const content = text.slice(cursor, fenceStart);
      if (content.trim()) segments.push({ type: "text", content });
    }

    const afterFence = fenceStart + 3;
    const newlineIdx = text.indexOf("\n", afterFence);
    const codeStart = newlineIdx === -1 ? afterFence : newlineIdx + 1;
    const fenceEnd = text.indexOf("```", codeStart);

    if (fenceEnd === -1) {
      const content = text.slice(codeStart);
      if (content.trim()) segments.push({ type: "code", content });
      break;
    }

    const content = text.slice(codeStart, fenceEnd);
    if (content.trim()) segments.push({ type: "code", content });

    cursor = fenceEnd + 3;
  }

  return segments;
};

  const isVideoMedia = (media) =>
    media?.type === "video" ||
    media?.file?.type?.startsWith("video/") ||
    (media?.url?.startsWith("blob:") && /\.(mp4|mov|avi|webm)(\?|$)/i.test(media?.name || "")) ||
    /\.(mp4|mov|avi|webm)$/i.test(media?.name || "");

  const isVideoEntry = (entry) =>
    Array.isArray(entry?.local_images) && entry.local_images.some(isVideoMedia);

  const cleanVideoStreamText = (value = "") => {
    const text = String(value)
      .replace(/^"+|"+$/g, "")
      .replace(/\\"/g, '"')
      .replace(/\\n/g, "\n");

    return text;
  };

  const appendVideoFrameChunk = (entry, frameUrl, output) => {
    const safeFrameUrl = frameUrl || "";
    const chunkText = cleanVideoStreamText(output);
    const existingFrames = Array.isArray(entry?.videoFrames) ? entry.videoFrames : [];
    const lastFrame = existingFrames[existingFrames.length - 1];
    const isStatusDone = String(output).trim() === "Status-Done";

    if (lastFrame && lastFrame.frame === safeFrameUrl) {
      const updatedFrame = {
        ...lastFrame,
        response: isStatusDone ? lastFrame.response : `${lastFrame.response || ""}${chunkText}`,
        done: isStatusDone || lastFrame.done,
      };

      return {
        ...entry,
        response: updatedFrame.response || entry.response,
        videoFrames: [...existingFrames.slice(0, -1), updatedFrame],
      };
    }

    const nextFrame = {
      frame: safeFrameUrl,
      response: isStatusDone ? "" : chunkText,
      done: isStatusDone,
    };

    return {
      ...entry,
      response: nextFrame.response,
      videoFrames: [...existingFrames, nextFrame],
    };
  };

  const tryParseVideoStreamPacket = (packet) => {
    const raw = String(packet || "").trim();
    if (!raw) return null;

    if (raw === "Status-Done" || raw === '"Status-Done"') {
      return "Status-Done";
    }

    const withoutPrefix = raw.startsWith("message:")
      ? raw.slice("message:".length).trim()
      : raw;

    if (!(withoutPrefix.startsWith("{") && withoutPrefix.endsWith("}"))) {
      return null;
    }

    const outputMatch = withoutPrefix.match(/'output'\s*:\s*'([\s\S]*?)'\s*,\s*'frame'/);
    const frameMatch = withoutPrefix.match(/'frame'\s*:\s*'([^']*)'/);

    if (!outputMatch && !frameMatch) {
      return null;
    }

    const output = (outputMatch?.[1] || "")
      .replace(/\\\\/g, "\\")
      .replace(/\\'/g, "'")
      .replace(/\\"/g, '"');

    const frame = frameMatch?.[1] || "";

    return { output, frame };
  };

  /* ------------------------------------------------------------------
   * EVALUATE — NEW EVALUATION CREATION
   * ------------------------------------------------------------------ */

  
  const setEvalBusyPrompt = (evalId, value) => {
    if (!evalId) return;

    setBusyPromptByEvalId(prev => ({
      ...prev,
      [evalId]: value
    }));
  };

  const isEvalBusyPrompt = !!busyPromptByEvalId?.[eval_id];



  const getTextGenerationPromptStep = (promptValue = promptText) => {
    if (!isTextGeneration || String(promptValue || "").trim()) {
      return null;
    }

    return {
      ref: promptRef,
      targetId: "llm-prompt-step",
      message: TEXT_PROMPT_REQUIRED_MESSAGE
    };
  };

  const getNextMissingStep = (promptValue = promptText) => {
    const textGenerationPromptStep = getTextGenerationPromptStep(promptValue);
    if (textGenerationPromptStep) return textGenerationPromptStep;

    const normalizedFamily = String(selectedFamily || "").trim();
    const normalizedChipset = String(selectedChipset || "").trim();
    const selectedMedia = normalizeMediaItems(selectedImages);

    if (!normalizedFamily) {
      return { ref: hardwareRef, targetId: "llm-hardware-step", message: "Please select a hardware family" };
    }

    if (!Object.prototype.hasOwnProperty.call(data?.devices || {}, normalizedFamily)) {
      return {
        ref: hardwareRef,
        targetId: "llm-hardware-step",
        message: `Selected hardware family "${normalizedFamily}" is no longer available.`
      };
    }

    if (!normalizedChipset) {
      return { ref: hardwareRef, targetId: "llm-hardware-step", message: "Please select a chipset" };
    }
    const validChipsets = Object.keys(data?.devices?.[normalizedFamily] || {});
    const chipsetExists = validChipsets.includes(normalizedChipset);

    if (!chipsetExists) {
      return {
        ref: hardwareRef,
        targetId: "llm-hardware-step",
        message: `Selected chipset "${normalizedChipset}" is not available in the device farm.`
      };
    }

    const availableDevices = data?.devices?.[normalizedFamily]?.[normalizedChipset];
    if (!Array.isArray(availableDevices) || availableDevices.length === 0) {
      return {
        ref: hardwareRef,
        targetId: "llm-hardware-step",
        message: `No devices are available for chipset "${normalizedChipset}".`
      };
    }

    if (selectedDevice && !availableDevices.includes(selectedDevice)) {
      return {
        ref: hardwareRef,
        targetId: "llm-hardware-step",
        message: `Selected device "${selectedDevice}" is no longer available.`
      };
    }

    if (compressionOptions.length > 0) {
      const normalizedCompression = String(selectedCompression ?? "").trim();
      if (!normalizedCompression) {
        return {
          ref: hardwareRef,
          targetId: "llm-hardware-step",
          message: "Please select a Compression"
        };
      }

      const compressionExists = compressionOptions.some(
        (option) => String(option.value) === normalizedCompression
      );
      if (!compressionExists) {
        return {
          ref: hardwareRef,
          targetId: "llm-hardware-step",
          message: "The selected compression is no longer available. Please select another option."
        };
      }
    }

    if (isVisualQuestionAnswering) {
      if (selectedMedia.length === 0) {
        if (supportsTextOnlyVisionInput && String(promptValue || "").trim()) {
          return null;
        }
        return {
          ref: supportsTextOnlyVisionInput ? promptRef : uploadRef,
          targetId: supportsTextOnlyVisionInput ? "llm-prompt-step" : "llm-upload-step",
          message: isFlorence
            ? "Please upload an image for Florence2"
            : supportsTextOnlyVisionInput
              ? "Please enter a prompt or upload at least one image"
              : "Please upload at least one image"
        };
      }
      return null;
    }

    if (isZeroShotTask) {
      if (selectedMedia.length === 0) {
        return { ref: uploadRef, targetId: "llm-upload-step", message: "Please upload images" };
      }
      if (!String(promptValue || "").trim()) {
        return {
          ref: promptRef,
          targetId: "llm-prompt-step",
          message:
            isZeroShotDetection
              ? "Please enter a prompt "
              : isZeroShotClassification
                ? "Please enter class names"
                : "Please enter a comma separated prompt"
        };
      }
      return null;
    }


    return null;
  };



  useEffect(() => {
    if (!guideActive) return;

    const missing = getNextMissingStep();

    if (missing) {
      highlightSection(missing.ref);
      showInfoPopover(missing.targetId, missing.message, 2200);
    } else {
      highlightSection(startBtnRef);
      setGuideActive(false);
    }
  }, [
    guideActive,
    selectedFamily,
    selectedChipset,
    selectedCompression,
    compressionOptions.length,
    selectedImages.length,
    taskType,
  ]);



  const attemptStartEvaluation = (promptOverride) => {
    if (loadButton || Creating || busy || isEvalBusyPrompt || startInFlightRef.current) return;

    setGuideActive(true);

    const promptForCheck =
      typeof promptOverride === "string" ? promptOverride : promptText;

    if (!data?.info?._id) {
      showInfoPopover("llm-prompt-step", "Model details are not ready yet. Please try again in a moment.", 3000);
      return;
    }

    if (suggestedImageSelectionInFlightRef.current) {
      showInfoPopover("llm-upload-step", "Please wait while the selected example images finish loading.", 3000);
      return;
    }

    const missing = getNextMissingStep(promptForCheck);
    if (missing) {
      highlightSection(missing.ref);
      showInfoPopover(missing.targetId, missing.message, 2500);
      return;
    }

    const submittedMedia = normalizeMediaItems(selectedImages);
    if (submittedMedia.some(isVideoMedia)) {
      showInfoPopover("llm-upload-step", "Video input is not supported right now.", 3000);
      setBusy(false);
      return;
    }
    
    const tokenLimitError = getTokenLimitError(promptForCheck, submittedMedia);
    if (tokenLimitError) {
      showInfoPopover("llm-prompt-step", tokenLimitError, 3000);
      setBusy(false);
      return;
    }

    setGuideActive(false);
    startInFlightRef.current = true;
    setBusy(true);
    handleEvaluate(promptOverride);
  };


  const handleEvaluate = async (promptOverride) => {
    let createdEvalId = null;

    const promptForCheck =
      typeof promptOverride === "string" ? promptOverride : promptText;
    const missing = getNextMissingStep(promptForCheck);
    if (missing) {
      highlightSection(missing.ref);
      showInfoPopover(missing.targetId, missing.message, 2500);
      setBusy(false);
      startInFlightRef.current = false;
      return;
    }

    const submittedMedia = normalizeMediaItems(selectedImages);
    if (submittedMedia.some(isVideoMedia)) {
      showInfoPopover("llm-upload-step", "Video input is not supported right now.", 3000);
      setBusy(false);
      startInFlightRef.current = false;
      return;
    }

    const tokenLimitError = getTokenLimitError(promptForCheck, submittedMedia);
    if (tokenLimitError) {
      showInfoPopover("llm-prompt-step", tokenLimitError, 3000);
      setBusy(false);
      startInFlightRef.current = false;
      return;
    }

    setLoadButton(true);
    if (window._ora) {
      window._ora.push([
        "trackEvent",
        {
          type: "button_click",
          name: `Started New Evaluation on ${selectedChipset}`,
          component: "Evaluation Page",
        }
      ]);
    }

    setCreating(true);
    update({ errorMessage: "" });

    try {
      const submitPrompt = String(
        typeof promptOverride === "string" ? promptOverride : promptText
      ).trim();
      const safeSelectedImages = submittedMedia.slice(
        0,
        isVisualQuestionAnswering
          ? (isFlorence ? 1 : MAX_VQA_IMAGES)
          : Number.POSITIVE_INFINITY
      );
   

      const firstImageName = safeSelectedImages?.[0]?.name;

      const hasVideoInput = safeSelectedImages?.some(
        (img) =>
          img?.type === "video" ||
          img?.file?.type?.startsWith("video/") ||
          /\.(mp4|mov|avi|webm)$/i.test(img?.name || "")
      );

      const hasImageInput = safeSelectedImages?.some(
        (img) =>
          img?.type === "image" ||
          img?.base64 ||
          img?.file?.type?.startsWith("image/")
      );

      const visualInputType = hasVideoInput ? "video" : hasImageInput ? "images" : undefined;



      const createPayload =
        isZeroShotTask
          ? {
            data_source: "BrowserUpload",
            target: "Registered_Device",
            device: selectedDevice ?? "",
            chipset: selectedChipset ?? "",
            family: selectedFamily ?? "",
            compression: compressionOptions.length > 0 ? selectedCompression : null,
            total_images: safeSelectedImages?.length,
            input_prompt: submitPrompt,
          }
          : {
            target: "Registered_Device",
            family: selectedFamily,
            chipset: selectedChipset,
            device: selectedDevice,
            compression: compressionOptions.length > 0 ? selectedCompression : null,
            input_prompt: submitPrompt,
            ...(firstImageName ? { image_name: firstImageName } : {}),
            total_images: safeSelectedImages?.length,
            ...(isVisualQuestionAnswering && visualInputType
              ? { input_type: visualInputType }
              : {}),
          };

      const result = await getDataFromApi(
        `${window.ENV.API_URL}/evaluations?model_id=${data?.info?._id}`,
        "POST",
        createPayload
      );

      if (!result?.evaluation_id) {
        throw new Error("The evaluation API did not return an evaluation ID.");
      }

      const evalId = result.evaluation_id;
      createdEvalId = evalId;

	      applySessionUpdate(prev => ({
	        ...prev,
	        pendingPredictionsByEvalId: {
	          ...(prev.pendingPredictionsByEvalId || {}),
	          [evalId]: {
	            evalId,
	            prompt: submitPrompt,
	            chatImage: safeSelectedImages?.[0] || null,
	            imageName: safeSelectedImages?.[0]?.name || "none",
	            chatImages: [...safeSelectedImages],
	            started: false,
	            queuedSeen: false,
	            queueRetryStarted: false,
	            turnId: null,
	          }
	        }
	      }), evalId);


      setLoadButton(false);
      
	      onEvaluationCreated(submitPrompt, evalId, safeSelectedImages);

	      if (isZeroShotTask) {
	        setPromptText("");
          if (isZeroShotClassification) {
            setLongclipClassNames([]);
            setLongclipClassDraft("");
          }
	        setSelectedDevice(null);
        setSelectedChipset(null);
      }


      
      const imgs = [...safeSelectedImages];

      update({ selectedImages: [] });

      // ---------------------------------------------------------
      // 2. UPLOAD IMAGES (zero-shot = S3, VQA = base64/video)
      // ---------------------------------------------------------
      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i];
        const isFirst = i === 0;
        const isLast = i === imgs.length - 1;

        const sanitizedImageName = sanitizeImageName(img.name);

        let uploadPayload = {
          _id: evalId,
          action: "data_upload",
          image_name: sanitizedImageName,
          input_prompt: submitPrompt || "",
          isFirst,
          iscomplete: isLast,
          task_type: taskType,
          model_id: data?.info?._id,
        };

        const isVideo =
          img?.type === "video" ||
          img?.file?.type?.startsWith("video/") ||
          /\.(mp4|mov|avi|webm)$/i.test(img?.name || "");

        const isImage =
          img?.type === "image" ||
          img?.base64 ||
          img?.file?.type?.startsWith("image/");

        
        if (isZeroShotTask) {
          const file = base64ToFile(img.base64, img.name);
          const keyName = await uploadToS3(file, evalId);
          uploadPayload.image_url = `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`;
        }

        
        else if (isVisualQuestionAnswering) {
          if (isImage) {
            uploadPayload.image = img.base64.split(",")[1];
          }
          if (isVideo) {
            let fileToUpload = img.file;

            
            if (!fileToUpload && img.base64) {
              fileToUpload = base64ToFile(img.base64, img.name);
            }

            if (fileToUpload) {
              const keyName = await uploadToS3(fileToUpload, evalId);
              uploadPayload.image_url = `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${keyName}`;
              uploadPayload.input_type = "video";
            }
          }
        }

        
        if (submitPrompt) {
          uploadPayload.prompt = submitPrompt;
        }


        await getDataFromApi(
          `${window.ENV.API_URL}/evaluations/action?visual_inspection=false`,
          "POST",
          uploadPayload
        );

      }

    } catch (err) {
      console.error("Evaluation failed", err);
      const message = getReadableError(err, "Unable to start evaluation. Please try again.");
      update({
        errorMessage: message,
        loading: false,
        responseStreaming: false,
        streamWait: false
      }, createdEvalId || activeSessionId);
      showInfoPopover("llm-prompt-step", message, 3500);
      setLoadButton(false);
    } finally {
      setLoadButton(false);
      setCreating(false);
      setBusy(false);
      startInFlightRef.current = false;
    }
  };

  const handleContextSelect = (contextNumber) => {
    const nextContext = normalizeContextNumber(contextNumber);
    if (!nextContext || nextContext === activeContext) return;

    applySessionUpdate(prev => {
      const currentContext = prev.activeContext || 1;
      const histories = {
        ...(prev.contextHistories || {}),
        [currentContext]: Array.isArray(prev.history) ? prev.history : [],
      };
      const nextHistory = Array.isArray(histories[nextContext])
        ? histories[nextContext]
        : [];
      const nextPagination = prev.contextPagination?.[nextContext] || {
        page: 1,
        totalPages: 1,
      };

      return {
        ...prev,
        activeContext: nextContext,
        history: nextHistory,
        page: nextPagination.page,
        totalPages: nextPagination.totalPages,
        contextHistories: histories,
        selectedImages: [],
        errorMessage: "",
      };
    }, eval_id);

    setPromptText("");
    setHistoryError(false);
    setLoadingHistory(false);
    autoScrollRef.current = true;

    const cachedContextHistory = session?.contextHistories?.[nextContext];
    const hasCachedContextHistory =
      Array.isArray(cachedContextHistory) && cachedContextHistory.length > 0;
    const isLocallyStartedEvaluation = eval_id === chatBoxPromptid;
    const shouldRequestContextHistory =
      !historyRequestedContextsRef.current.has(nextContext) &&
      !hasCachedContextHistory &&
      !isLocallyStartedEvaluation;

    if (shouldRequestContextHistory) {
      handleprevpredictions(eval_id, 1, {
        context: nextContext,
        initialScroll: "bottom",
        preserveStreamWait:
          streamWait && nextContext !== streamingContext,
      });
    } else {
      setTimeout(() => scrollToBottom(false, true), 0);
    }
  };

  const handleCreateContext = async () => {
    if (
      !eval_id ||
      !isEvaluating ||
      !latestContextHasHistory ||
      contextCreationInFlightRef.current ||
      evaluationActionDisabled ||
      isLocked
    ) return;

    contextCreationInFlightRef.current = true;
    setIsCreatingContext(true);
    setResettingContext(true);

    try {
      const result = await getDataFromApi(
        `${window.ENV.API_URL}/evaluations/action?visual_inspection=false`,
        "POST",
        { _id: eval_id, action: "reset" }
      );

      applySessionUpdate(prev => {
        const currentContext = prev.activeContext || 1;
        const knownContexts = normalizeAvailableContexts(
          [
            ...(prev.availableContexts || []),
            ...(Array.isArray(result?.available_contexts)
              ? result.available_contexts
              : []),
          ],
          currentContext
        );
        const responseContext = normalizeContextNumber(
          result?.context ?? result?.context_file
        );
        const nextContext =
          responseContext && responseContext > currentContext
            ? responseContext
            : Math.max(...knownContexts, currentContext) + 1;

        return {
          ...prev,
          activeContext: nextContext,
          availableContexts: normalizeAvailableContexts(
            [...knownContexts, nextContext],
            nextContext
          ),
          history: [],
          loading: false,
          streamWait: false,
          responseStreaming: false,
          page: 1,
          totalPages: 1,
          contextHistories: {
            ...(prev.contextHistories || {}),
            [currentContext]: Array.isArray(prev.history) ? prev.history : [],
            [nextContext]: [],
          },
          contextPagination: {
            ...(prev.contextPagination || {}),
            [nextContext]: { page: 1, totalPages: 1 },
          },
          selectedImages: [],
          pendingPrompt: "",
          errorMessage: "",
        };
      }, eval_id);

      setPromptText("");
      setActiveExample("");
      setHistoryError(false);
      setLoadingHistory(false);
      autoScrollRef.current = true;
      showInfoPopover(
        "llm-context-tabs",
        "New context created. You can continue the evaluation here.",
        2500
      );
    } catch (err) {
      const errorMessage = getReadableError(
        err,
        "Unable to create a new model context. Please try again."
      );
      update({ errorMessage }, eval_id);
      showInfoPopover("llm-context-tabs", errorMessage, 3500);
    } finally {
      contextCreationInFlightRef.current = false;
      setIsCreatingContext(false);
      setResettingContext(false);
    }
  };


  /* ------------------------------------------------------------------
   * STREAMING SSE (PARALLEL-SAFE)
   * ------------------------------------------------------------------ */
  const calledEvalIdsRef = useRef(new Set());

  const handlepredictions = async (evalId, promptText, chat_image, image_name, chat_images = [], source, retryTurnId = null) => {


    if (!evalId) return;

    const isReentryBackgroundStream =
      source === "auto" && isEvaluationReentryRef.current;

    // A new response starts in follow mode. Content growth can create the
    // scrollbar while it is still at the top; that must not be mistaken for
    // the user choosing to stay there.
    const scrollElement = getScrollElement();
    autoScrollRef.current = true;
    lastScrollTopRef.current = scrollElement?.scrollTop || 0;

    const predictionContext = isReentryBackgroundStream
      ? latestContext
      : normalizeContextNumber(session?.activeContext) || 1;
    const suppliedStreamPrompt = String(promptText ?? "");
    const pendingEvaluationPrompt = String(
      session?.pendingPredictionsByEvalId?.[evalId]?.prompt ?? ""
    );
    const streamPrompt = suppliedStreamPrompt.trim()
      ? suppliedStreamPrompt
      : source === "initial"
        ? pendingEvaluationPrompt
        : suppliedStreamPrompt;
    const skipStreamingRef = { current: false };
    let reentryHistoryRefreshStarted = false;
    const applyPredictionSessionUpdate = (updates) =>
      applySessionUpdate(prev => {
        const isPredictionContextActive =
          (prev.activeContext || 1) === predictionContext;
        const predictionHistory = isPredictionContextActive
          ? (Array.isArray(prev.history) ? prev.history : [])
          : (
              Array.isArray(prev.contextHistories?.[predictionContext])
                ? prev.contextHistories[predictionContext]
                : []
            );
        const scopedPrev = {
          ...prev,
          history: predictionHistory,
        };
        const resolved =
          typeof updates === "function"
            ? updates(scopedPrev)
            : { ...scopedPrev, ...(updates || {}) };

        if (!resolved) return prev;

        const nextHistory = Array.isArray(resolved.history)
          ? resolved.history
          : predictionHistory;

        return {
          ...resolved,
          streamingContext: predictionContext,
          history: isPredictionContextActive ? nextHistory : prev.history,
          contextHistories: {
            ...(prev.contextHistories || {}),
            ...(resolved.contextHistories || {}),
            [predictionContext]: nextHistory,
          },
        };
      }, evalId);
    const updatePredictionSession = (updates) => update(updates, evalId);
    const setPredictionStreamWait = (value) => setSessionStreamWait(value, evalId);
    const refreshReentryContextHistory = () => {
      if (!isReentryBackgroundStream || reentryHistoryRefreshStarted) return;

      reentryHistoryRefreshStarted = true;
      historyRequestedContextsRef.current.delete(predictionContext);
      handleprevpredictions(evalId, 1, {
        context: predictionContext,
        initialScroll: "bottom",
      });
    };

    
    if (source === "auto") {
      setBusy(true);
      // setStreamWait(true);
      setPredictionStreamWait(true);
      if (isReentryBackgroundStream) {
        applySessionUpdate(prev => {
          const contextHistory = Array.isArray(
            prev.contextHistories?.[predictionContext]
          )
            ? prev.contextHistories[predictionContext]
            : [];

          return {
            ...prev,
            activeContext: predictionContext,
            availableContexts: normalizeAvailableContexts(
              [...(prev.availableContexts || []), ...normalizedContexts],
              predictionContext
            ),
            history: contextHistory,
            streamingContext: predictionContext,
            loading: true,
            responseStreaming: true,
          };
        }, evalId);
      }
      if (calledEvalIdsRef.current.has(evalId)) {
        return;
      }
    }

    closeRegisteredStream(evalId);
    
    calledEvalIdsRef.current.add(evalId);


    const turnId = retryTurnId ?? Date.now();
    const rawChatImages =
      Array.isArray(chat_images) && chat_images.length > 0
        ? chat_images
        : (chat_image ? [chat_image] : []);
    const safeChatImages = normalizeMediaItems(rawChatImages);
    const safeChatImage = safeChatImages[0] || null;
    const safeImageName =
      safeChatImages[0]?.name ||
      (image_name && image_name !== "none" ? sanitizeImageName(image_name) : "none");
    const zeroShotPromptImages = safeChatImages.map((img) => ({
      imageUrl: img.base64 || img.imageUrl || img.url || "",
      imageName: img.name,
      thumbUrl: img.thumbUrl || null,
      predictions: [],
      scores: [],
      boxes: [],
    }));

    applyPredictionSessionUpdate(prev => ({
      ...prev,
      pendingPredictionsByEvalId: {
        ...(prev.pendingPredictionsByEvalId || {}),
        [evalId]: {
          ...prev.pendingPredictionsByEvalId?.[evalId],
          evalId,
          prompt:
            streamPrompt ||
            prev.pendingPredictionsByEvalId?.[evalId]?.prompt ||
            "",
          chatImage: safeChatImage ?? prev.pendingPredictionsByEvalId?.[evalId]?.chatImage ?? null,
          imageName: safeImageName ?? prev.pendingPredictionsByEvalId?.[evalId]?.imageName ?? "none",
          chatImages: safeChatImages?.length
            ? safeChatImages
            : (prev.pendingPredictionsByEvalId?.[evalId]?.chatImages || []),
          started: true,
          turnId,
          queueRetryStarted:
            source === "retry"
              ? true
              : prev.pendingPredictionsByEvalId?.[evalId]?.queueRetryStarted ?? false,
        }
      },
      responseStreaming: true,
    }));
    applyPredictionSessionUpdate(prev => {
      const history = Array.isArray(prev.history) ? [...prev.history] : [];
      const index = history.findIndex(h => h.turnId === turnId);

      if (index !== -1) {
        // 🔁 RETRY: reuse existing entry
        history[index] = {
          ...history[index],
          prompt: history[index]?.prompt || streamPrompt,
          response: STREAM_STATUS.ANALYSING,
          retry: false,
          ...(Array.isArray(safeChatImages) && safeChatImages.some(isVideoMedia)
            ? { videoFrames: [] }
            : {})
        };
      } else {
        
        // history.push({
        //   turnId,
        //   prompt: promptText,
        //   response: STREAM_STATUS.ANALYSING,
        //   ...(chat_image?.base64 ? { local_image: chat_image.base64 } : {})
        // });
        history.push({
          turnId,
          prompt: streamPrompt,
          response: STREAM_STATUS.ANALYSING,
          ...(Array.isArray(safeChatImages) && safeChatImages.some(isVideoMedia)
            ? { videoFrames: [] }
            : {}),
          ...(isZeroShotTask
            ? {
              images: zeroShotPromptImages,
              imagePrompt: streamPrompt ? [{ prompt: streamPrompt }] : [],
            }
            : Array.isArray(safeChatImages) && safeChatImages.length > 0
            ? {
              local_images: safeChatImages.map(img => ({
                // base64: img.base64,
                // name: img.name
                name: img.name,
                type: img.type || "image",
                base64: img.base64 || null,
                url: img.url || img.base64 || null,
                thumbUrl: img.thumbUrl || null,
              }))
            }
            : safeChatImage?.base64
              ? { local_image: safeChatImage.base64 }
              : {})
        });
      }

      return { ...prev, history };
    });


    setEvalBusyPrompt(evalId, true);
    if (isZeroShotTask) {
      updatePredictionSession({
        loading: true,
        pendingPrompt: streamPrompt,
        forPrompt: true,
      });
    } else {
      updatePredictionSession({
        loading: true,
        pendingPrompt: streamPrompt,
        forPrompt: true,
      });

    }


    const token = getCokkie("jwtToken");
    const tenant_id = getTenantId();
    let url;
    const baseUrl = `${window.ENV.API_URL}/evaluations`;

    const filters = encodeURIComponent(JSON.stringify({ _id: evalId }));

    let imageNames = [];

    if (Array.isArray(safeChatImages) && safeChatImages.length > 0) {
      imageNames = safeChatImages
        .map(img => img?.name)
        .filter(Boolean)
        .map(name => sanitizeImageName(name));
    } else if (safeImageName && safeImageName !== "none") {
      imageNames = [safeImageName];
    }

    const imagesParam =
      imageNames.length > 0
        ? `&images=${encodeURIComponent(JSON.stringify(imageNames))}`
        : `&images=[]`;


    if (!session.firstEvaluation) {
      url = `${baseUrl}?filters=${filters}&section=predictions&next_prompt=true${imagesParam}&tenant_id=${tenant_id}`;
    } else {
      url = `${baseUrl}?filters={"_id":"${evalId}"}&section=predictions&next_prompt=false${imagesParam}&tenant_id=${tenant_id}`;
      updatePredictionSession({ firstEvaluation: false });
    }

    // ---- TYPING LOGIC ----
    const TICK_MS = 35;
    let wordQueue = [];
    let typingTimer = null;
    let currentResponse = "";

    const tokenize = (text) => text.match(/\s+|[^\s]+/g) || [];
    const getCurrent = () => currentResponse;
    const setCurrent = (v) => (currentResponse = v);

    const ensureEntry = () => {
      applyPredictionSessionUpdate(prev => {
        const history = Array.isArray(prev.history) ? [...prev.history] : [];
        const exists = history.some(h => h.turnId === turnId);

        if (!exists) {
          history.push({
            turnId,
            prompt: streamPrompt,
            response: "",
            ...(safeChatImage?.base64 ? { local_image: safeChatImage.base64 } : {})
          });
        }

        return { ...prev, history };
      });
    };



    const flushRemainingTokens = () => {
      if (typingTimer) {
        clearInterval(typingTimer);
        typingTimer = null;
        registerStreamHandle(evalId, { typingTimer: null });
      }

      if (wordQueue.length > 0) {
        currentResponse += wordQueue
          .filter(t => typeof t === "string")
          .join("");

        wordQueue = [];
      }

      applyPredictionSessionUpdate(prev => {
        const history = Array.isArray(prev.history) ? [...prev.history] : [];
        const last = history[history.length - 1];
        if (last && last.turnId === turnId) {
          last.response = currentResponse;
        }
        return { ...prev, history };
      });
    };



    const startTyping = () => {
      if (typingTimer || skipStreamingRef.current) return;

      typingTimer = setInterval(() => {
        if (skipStreamingRef.current && wordQueue.length === 0) {
          clearInterval(typingTimer);
          typingTimer = null;
          registerStreamHandle(evalId, { typingTimer: null });
          return;
        }

      if (wordQueue.length === 0) {
          clearInterval(typingTimer);
          typingTimer = null;
          registerStreamHandle(evalId, { typingTimer: null });
          return;
        }

        const token = wordQueue.shift();
        // if (typeof token !== "string") {
        //   return;
        // }
        const next = getCurrent() + token;
        setCurrent(next);
        // setEvalBusyPrompt(evalId, true);
        // updatePredictionSession({
        //   history: (() => {
        //     const list = [...session.history];
        //     const last = list[list.length - 1];
        //     if (last && last.turnId === turnId) {
        //       last.response = next;
        //     } else {
        //       list.push({ turnId, prompt: promptText, response: next, ...(chat_image?.base64 ? { local_image: chat_image.base64 } : {}), });
        //     }
        //     return list;
        //   })()
        // });

        applyPredictionSessionUpdate(prev => {
          const history = Array.isArray(prev.history) ? [...prev.history] : [];
          const idx = history.findIndex(h => h.turnId === turnId);

          if (idx !== -1) {
            history[idx] = { ...history[idx], response: next };
          }

          return { ...prev, history };
        });


        if (autoScrollRef.current) {
          scrollToBottom(false);
        }


      }, TICK_MS);
      registerStreamHandle(evalId, { typingTimer });
    };

    // ---- SSE ----
    const MAX_SSE_RETRIES = 3;
    const SSE_RETRY_DELAYS_MS = [1000, 2500, 5000];
    let sseRetryCount = 0;
    let sseRetryTimer = null;
    let streamCompleted = false;
    let replayGuardText = "";
    let replayGuardOffset = 0;
    let es = null;

    const closeSse = () => {
      if (es) {
        es.close();
        es = null;
        registerStreamHandle(evalId, { eventSource: null });
      }
    };

    const clearSseRetryTimer = () => {
      if (sseRetryTimer) {
        clearTimeout(sseRetryTimer);
        sseRetryTimer = null;
        registerStreamHandle(evalId, { retryTimer: null });
      }
    };

    const markStreamComplete = () => {
      streamCompleted = true;
      clearSseRetryTimer();
      closeSse();
      ACTIVE_STREAMS_BY_EVAL_ID.delete(evalId);
    };

    const prepareReplayGuard = () => {
      flushRemainingTokens();
      replayGuardText = currentResponse || "";
      replayGuardOffset = 0;
    };

    const stripReplayedChunk = (chunk) => {
      if (!replayGuardText || replayGuardOffset >= replayGuardText.length) {
        return chunk;
      }

      const expected = replayGuardText.slice(
        replayGuardOffset,
        replayGuardOffset + chunk.length
      );

      if (expected === chunk) {
        replayGuardOffset += chunk.length;
        return "";
      }

      if (chunk.startsWith(expected)) {
        replayGuardOffset = replayGuardText.length;
        return chunk.slice(expected.length);
      }

      replayGuardText = "";
      replayGuardOffset = 0;
      return chunk;
    };

    const finalizeFailedStream = (error) => {
      const errorMessage = getReadableError(
        error,
        "The response stream was interrupted. You can retry this prompt."
      );
      setPredictionStreamWait(false);
      setBusy(false);
      setEvalBusyPrompt(evalId, false);

      applyPredictionSessionUpdate(prev => {
        const history = Array.isArray(prev.history) ? [...prev.history] : [];
        const idx = history.findIndex(h => h.turnId === turnId);
        const lastIdx = idx !== -1 ? idx : history.length - 1;
        const last = history[lastIdx];

        if (last?.response === STREAM_STATUS.ANALYSING) {
          history[lastIdx] = {
            ...last,
            response: NO_RESPONSE_MESSAGE,
            retry: true,
          };
        }

        return {
          ...prev,
          history,
          loading: false,
          pendingPrompt: "",
          responseStreaming: false,
          errorMessage
        };
      });
    };

    const scheduleSseRetry = (err) => {
      if (streamCompleted || skipStreamingRef.current) return;

      closeSse();
      prepareReplayGuard();

      if (sseRetryCount >= MAX_SSE_RETRIES) {
        console.error("SSE retries exhausted:", err);
        handleprevpredictions(evalId, 1, { preserveStreaming: true })
          .finally(() => {
            finalizeFailedStream(err);
            if (source === "auto") {
              calledEvalIdsRef.current.delete(evalId);
            }
          });
        return;
      }

      const retryDelay =
        SSE_RETRY_DELAYS_MS[sseRetryCount] ||
        SSE_RETRY_DELAYS_MS[SSE_RETRY_DELAYS_MS.length - 1];

      sseRetryCount += 1;
      setBusy(true);
      setEvalBusyPrompt(evalId, true);
      applyPredictionSessionUpdate(prev => ({
        ...prev,
        loading: true,
        responseStreaming: true
      }));

      sseRetryTimer = setTimeout(() => {
        sseRetryTimer = null;
        registerStreamHandle(evalId, { retryTimer: null });
        connectSse();
      }, retryDelay);
      registerStreamHandle(evalId, { retryTimer: sseRetryTimer });
    };

    function connectSse() {
      try {
        es = new EventSourcePolyfill(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          heartbeatTimeout: 500000
        });
      } catch (error) {
        scheduleSseRetry(error);
        return;
      }
      registerStreamHandle(evalId, { eventSource: es });

    es.onopen = () => {
      applyPredictionSessionUpdate(prev => ({ ...prev, errorMessage: "" }));
    };

    es.onerror = (err) => {
      console.error("SSE ERROR:", err);
      scheduleSseRetry(err);
    };

    es.onmessage = (event) => {
      updatePredictionSession({ forPrompt: false });

      let packet = event.data;
      if (!packet) return;

      let parsed1 = normalizeStreamPacket(packet);
      if (typeof parsed1 === "string" && isVisualQuestionAnswering) {
        const fallbackVideoPacket = tryParseVideoStreamPacket(parsed1);
        if (fallbackVideoPacket) parsed1 = fallbackVideoPacket;
      }

      if (parsed1 === STREAM_STATUS.DONE) {
            const wasAutoStarted = autoStartedEvalRef.current.has(evalId);

            skipStreamingRef.current = true;

            markStreamComplete();
            flushRemainingTokens();

            setBusy(false);
            setPredictionStreamWait(false);
            setEvalBusyPrompt(evalId, false);

            applyPredictionSessionUpdate(prev => ({
              ...prev,
              loading: false,
              pendingPrompt: "",
              responseStreaming: false,
            }));
            refreshContextMetadata(evalId, predictionContext);
            if (source === "auto" && wasAutoStarted) {
              autoStartedEvalRef.current.delete(evalId);
              calledEvalIdsRef.current.delete(evalId);
              if (isReentryBackgroundStream) {
                refreshReentryContextHistory();
              } else {
                handleprevpredictions(evalId, 1, {
                  context: predictionContext,
                });
              }
            }

            return;
      }

      if (parsed1 == null || parsed1 === "") return;


      
      if (typeof parsed1 === "string" && parsed1.startsWith("data:")) {
        parsed1 = parsed1.replace(/^data:\s*/, "");
      }

      
      if (isZeroShotTask) {
        if (parsed1?.status === "done") {
          markStreamComplete();
          setBusy(false);
          setPredictionStreamWait(false);
          setEvalBusyPrompt(evalId, false);
          applyPredictionSessionUpdate(prev => ({
            ...prev,
            loading: false,
            pendingPrompt: "",
            responseStreaming: false,
          }));
          return;
        }

        applyPredictionSessionUpdate(prev => ({
          ...prev,
          loading: false,
        }));

        const raw = parsed1?.session_history ?? parsed1?.predictions ?? parsed1;
        const arr = (Array.isArray(raw) ? raw : [raw]).filter(
          (item) => item && typeof item === "object"
        );
        if (arr.length === 0) return;

        const mapped = arr.map((item) => {
          const imgs = Object.entries(item || {})?.map(([name, d]) => {
            const ptxt = d?.text_prompt || "";
            const labels = ptxt.split(",")?.map((x) => x.trim());
            const scoreList = Array.isArray(d?.score) ? d.score : [];

            let scores = [];
            if (Array.isArray(d?.annotation)) {
              const row = d.annotation.find(
                (a) => Array.isArray(a) && a.every((v) => typeof v === "string" || typeof v === "number")
              );
              if (row) scores = row;
            }

            return {
              imageName: d?.image_name || name,
              imageUrl: d?.image_url || "",
              scores: scoreList,
              predictions: labels?.map((label, i) => ({
                label,
                score: scores[i] !== undefined ? parseFloat(scores[i])?.toFixed(4) : "N/A"
              })),
              boxes: d?.annotation || [],
            };
          });

          return {
            imagePrompt: imgs?.map((x) => ({ prompt: x.predictions?.map((p) => p.label)?.join(", ") })),
            images: imgs
          };
        });

        applyPredictionSessionUpdate(prev => ({
          ...prev,
          history: [...(Array.isArray(prev.history) ? prev.history : []), ...mapped]
        }));
        return;
      }

      
      let parsed2;
      try {
        parsed2 = JSON.parse(parsed1);
      } catch {
        
        parsed2 = parsed1;
      }

      const florenceSpatial = isFlorence
        ? getFlorenceSpatialPayload(parsed2)
        : null;

      if (florenceSpatial) {
        if (isReentryBackgroundStream) return;

        if (typingTimer) {
          clearInterval(typingTimer);
          typingTimer = null;
          registerStreamHandle(evalId, { typingTimer: null });
        }
        wordQueue = [];
        currentResponse = "";
        setEvalBusyPrompt(evalId, true);
        setPredictionStreamWait(false);

        applyPredictionSessionUpdate(prev => {
          const history = Array.isArray(prev.history) ? [...prev.history] : [];
          const idx = history.findIndex(h => h.turnId === turnId);

          if (idx !== -1) {
            history[idx] = {
              ...history[idx],
              response: "",
              image_url: florenceSpatial.imageUrl,
              florenceSpatial,
            };
          }

          return { ...prev, history, loading: false };
        });

        if (autoScrollRef.current) scrollToBottom(false);
        return;
      }

      if (
        isVisualQuestionAnswering &&
        parsed2 &&
        typeof parsed2 === "object" &&
        !Array.isArray(parsed2) &&
        ("output" in parsed2 || "frame" in parsed2)
      ) {
        const rawOutput = parsed2?.output;
        const frameUrl = parsed2?.frame || "";

        if (rawOutput === "Status-Done") {
          markStreamComplete();
          setBusy(false);
          if (!isReentryBackgroundStream) {
            setPredictionStreamWait(false);
          }
          setEvalBusyPrompt(evalId, false);
          applyPredictionSessionUpdate(prev => {
            const history = Array.isArray(prev.history) ? [...prev.history] : [];
            const idx = history.findIndex(h => h.turnId === turnId);

            if (idx !== -1) {
              history[idx] = appendVideoFrameChunk(history[idx], frameUrl, rawOutput);
            }

            return {
              ...prev,
              history,
              loading: false,
              pendingPrompt: "",
              responseStreaming: false,
            };
          });
          refreshReentryContextHistory();
          return;
        }

        if (isReentryBackgroundStream) return;

        setEvalBusyPrompt(evalId, true);

        applyPredictionSessionUpdate(prev => {
          const history = Array.isArray(prev.history) ? [...prev.history] : [];
          const idx = history.findIndex(h => h.turnId === turnId);

          if (idx !== -1) {
            history[idx] = appendVideoFrameChunk(history[idx], frameUrl, rawOutput);
          }

          return { ...prev, history };
        });

        if (autoScrollRef.current) {
          scrollToBottom(false);
        }

        return;
      }

      // Unwrap quoted chunks: "\"T\"" → "T"
      if (typeof parsed2 === "string") {
        parsed2 = parsed2.replace(/^"(.*)"$/, "$1");
        parsed2 = parsed2.replace(/\\n/g, "\n");
      } else if (parsed2 == null) {
        return;
      } else {
        parsed2 = String(parsed2);
      }

      // TERMINATION
      if (parsed2 === "Status-Done") {
        const wasAutoStarted = autoStartedEvalRef.current.has(evalId);

        skipStreamingRef.current = true;

        markStreamComplete();

        flushRemainingTokens();

        setBusy(false);
        // setStreamWait(false);
        setPredictionStreamWait(false);
        setEvalBusyPrompt(evalId, false);

        applyPredictionSessionUpdate(prev => ({
          ...prev,
          loading: false,
          pendingPrompt: "",
          responseStreaming: false
        }));

        if (source === "auto" && wasAutoStarted) {
          autoStartedEvalRef.current.delete(evalId);
          calledEvalIdsRef.current.delete(evalId);
          applyPredictionSessionUpdate(prev => ({ ...prev, loading: false, pendingPrompt: "", responseStreaming: false }));
          if (isReentryBackgroundStream) {
            refreshReentryContextHistory();
          } else {
            handleprevpredictions(evalId, 1, {
              context: predictionContext,
            });
          }
          return;
        }

        return;
      }



      // STREAMING TEXT
      const streamChunk = stripReplayedChunk(parsed2);
      if (!streamChunk) {
        return;
      }
      const tokens = tokenize(streamChunk);

      if (tokens.length > 0) {
        if (skipStreamingRef.current || isReentryBackgroundStream) return;

        setEvalBusyPrompt(evalId, true);

        ensureEntry();
        wordQueue.push(...tokens);
        startTyping();
      }
    };

   
    es.addEventListener("done", () => {
      setBusy(false);
      if (!isReentryBackgroundStream) {
        setPredictionStreamWait(false);
      }
      setEvalBusyPrompt(evalId, false);
      applyPredictionSessionUpdate(prev => ({
        ...prev,
        loading: false,
        pendingPrompt: "",
        responseStreaming: false     // ⭐
      }));
      markStreamComplete();

      if (typingTimer) {
        clearInterval(typingTimer);
        typingTimer = null;
        registerStreamHandle(evalId, { typingTimer: null });
      }

      applyPredictionSessionUpdate(prev => {
        const history = Array.isArray(prev.history) ? [...prev.history] : [];
        const idx = history.findIndex((item) => item.turnId === turnId);
        if (idx !== -1) {
          history[idx] = {
            ...history[idx],
            response: currentResponse.trim() || history[idx].response,
          };
        } else if (currentResponse.trim()) {
          history.push({ turnId, prompt: streamPrompt, response: currentResponse.trim() });
        }
        return { ...prev, history };
      });
      autoStartedEvalRef.current.delete(evalId);
      calledEvalIdsRef.current.delete(evalId);
      refreshReentryContextHistory();
    });

    }

    connectSse();
  };
  const handleprevpredictions = async (evalId, pageNumber = 1, options = {}) => {
    if (!evalId) return;
    if (pageNumber > 1 && pagingRequestInFlightRef.current) return;

    const requestedContext =
      normalizeContextNumber(options?.context) || activeContext || 1;
    historyRequestedContextsRef.current.add(requestedContext);
    const applyHistorySessionUpdate = (updates) => applySessionUpdate(updates, evalId);
    const updateHistorySession = (updates) => update(updates, evalId);
    const setHistoryStreamWait = (value) => setSessionStreamWait(value, evalId);
    const initialScroll = options?.initialScroll || "bottom";
    const preserveStreamWait = Boolean(options?.preserveStreamWait);
    const shouldOpenAtTop = pageNumber === 1 && initialScroll === "top";
    const shouldPreservePrependScroll = pageNumber > 1;
    const prependScrollAnchor = shouldPreservePrependScroll
      ? getPrependScrollAnchor()
      : null;

    if (shouldPreservePrependScroll) {
      pagingRequestInFlightRef.current = true;
      autoScrollRef.current = false;
    }

    setLoadingHistory(true);

    if (window._ora) {
      window._ora.push([
        "trackEvent",
        {
          type: "card_click",
          name: "Archived Evaluation",
          component: "Evaluation Page",
        }
      ]);
    }

    updateHistorySession({ loading: true, errorMessage: "" });

    const pageSize = 5;
    const historyFilters = {
      _id: evalId,
      ...(supportsContextTabs
        ? { context: requestedContext }
        : {}),
    };
    const url = `${window.ENV.API_URL}/evaluations?filters=${encodeURIComponent(
      JSON.stringify(historyFilters)
    )}&section=session_history&page=${pageNumber}&page_size=${pageSize}&is_stream=false&visual_inspection=false`;

    try {



      setHistoryError(false);
      // const payload = await response.json();
      const payload = await getDataFromApi(url, "GET");


      const fetched = payload.predictions ?? payload;
      const totalPages = payload.total_pages || 1;
      const {
        contextChatSummaries: responseContextSummaries,
        availableContexts: metadataContexts,
      } = getContextMetadata(payload, evalId);
      const responseContexts = normalizeAvailableContexts(
        [
          ...(Array.isArray(payload?.available_contexts) ? payload.available_contexts : []),
          ...metadataContexts,
        ],
        requestedContext
      );

      setLoadingHistory(false);

      setPromptDuplicate(payload?.prompt)

      // console.log('dumy promt', payload?.prompt)

      let parsedEntries = [];

      // ------------------------------------------------------------------
      // ✔ CASE 1 — zero-shot tasks (complex multi-image structure)
      // ------------------------------------------------------------------
      if (isZeroShotTask && Array.isArray(fetched)) {

        parsedEntries = fetched?.map((item) => {
          
          const firstLayer = Object.values(item)?.[0];
          const secondLayer = firstLayer ? Object.values(firstLayer)?.[0] : null;
          const annotations = secondLayer?.annotations || {};

          const images = Object.entries(annotations)?.map(([imageName, data]) => {
            const prompt = data?.text_prompt || Object.keys(firstLayer)?.[0] || "";

            const labels = prompt.split(",")?.map((l) => l.trim());
            const scoreList = Array.isArray(data?.score) ? data.score : [];


            let scores = [];
            if (Array.isArray(data?.annotation)) {
              const row = data.annotation.find(
                (a) =>
                  Array.isArray(a) &&
                  a.every((v) => typeof v === "string" || typeof v === "number")
              );
              if (row) scores = row;
            }

            return {
              imageName,
              imageUrl: data?.image_url,
              scores: scoreList,
              predictions: labels?.map((label, i) => ({
                label,
                score:
                  scores[i] !== undefined
                    ? Number(scores[i]).toFixed(4)
                    : "N/A"
              })),
              boxes: data?.annotation
            };
          });

          return {
            images,
            imagePrompt: images?.map((img) => ({
              prompt: img.predictions?.map((p) => p.label).join(", ")
            }))
          };
        });
      }

      // ------------------------------------------------------------------
      // ✔ CASE 2 — VQA and text generation tasks (simple structure)
      // ------------------------------------------------------------------
      else if (Array.isArray(fetched)) {
        parsedEntries = fetched?.map((entry) => {
          const florenceSpatial = isFlorence
            ? getFlorenceSpatialPayload(entry)
            : null;
          const imageUrls = getImageUrls(entry?.image_url ?? entry?.imageUrl);
          const ignoredResponseKeys = isFlorence
            ? [
                "performance",
                "image_url",
                "imageUrl",
                "box",
                "bbox",
                "segment",
                "segmentation",
                "embedded_generation_time_sec",
                "time_taken_per_first_token",
                "token_per_second",
              ]
            : ["performance", "image_url"];
          const [prompt, response] =
            Object.entries(entry).find(
              ([k]) => !ignoredResponseKeys.includes(k)
            ) || [];

          let parsedVideoFrames = [];
          if (Array.isArray(response)) {
            parsedVideoFrames = response
              .filter((item) => item && typeof item === "object" && item.frame)
              .map((item) => ({
                frame: item.frame,
                response: cleanVideoStreamText(item.output || item.response || ""),
                done: true,
              }));
          }

          const nextEntry = {
            prompt: isFlorence
              ? String(prompt || "").replace(/_/g, " ")
              : (prompt || ""),
            response: florenceSpatial ? "" : (response || ""),
            image_url: imageUrls[0] || null,
            image_urls: imageUrls,
          };

          if (florenceSpatial) {
            nextEntry.image_url = florenceSpatial.imageUrl;
            nextEntry.image_urls = [florenceSpatial.imageUrl];
            nextEntry.florenceSpatial = florenceSpatial;
          }

          if (parsedVideoFrames.length > 0) {
            nextEntry.videoFrames = parsedVideoFrames;
            nextEntry.response = parsedVideoFrames[parsedVideoFrames.length - 1]?.response || "";
          }

          return nextEntry;
        });
      }

      // ------------------------------------------------------------------
      // MERGE INTO SESSION HISTORY
      // ------------------------------------------------------------------
      const preserveStreaming = Boolean(options?.preserveStreaming);

      if (shouldOpenAtTop) {
        autoScrollRef.current = false;
      } else if (pageNumber === 1) {
        autoScrollRef.current = true;
      }

      applyHistorySessionUpdate(prev => {
        const currentHistory = Array.isArray(prev.history) ? prev.history : [];
        const storedContextHistory = Array.isArray(
          prev.contextHistories?.[requestedContext]
        )
          ? prev.contextHistories[requestedContext]
          : [];
        const streamingEntries = preserveStreaming && parsedEntries.length === 0
          ? currentHistory.filter((entry) => entry?.response === STREAM_STATUS.ANALYSING)
          : [];
        const baseHistory =
          pageNumber === 1
            ? parsedEntries
            : [
                ...parsedEntries,
                ...(prev.activeContext === requestedContext
                  ? currentHistory
                  : storedContextHistory),
              ];
        const merged = [...baseHistory];

        streamingEntries.forEach((entry) => {
          const alreadyExists = entry?.turnId
            ? merged.some((item) => item?.turnId === entry.turnId)
            : merged.includes(entry);

          if (!alreadyExists) {
            merged.push(entry);
          }
        });

        const hasResponseContextMetadata =
          (Array.isArray(payload?.available_contexts) && payload.available_contexts.length > 0) ||
          Object.keys(responseContextSummaries).length > 0;
        const nextAvailableContexts = hasResponseContextMetadata
          ? normalizeAvailableContexts(
              [...(prev.availableContexts || []), ...responseContexts],
              requestedContext
            )
          : normalizeAvailableContexts(
              [...(prev.availableContexts || []), requestedContext],
              requestedContext
            );
        const isRequestedContextActive =
          (prev.activeContext || 1) === requestedContext;

        return {
          ...prev,
          history: isRequestedContextActive ? merged : currentHistory,
          loading: false,
          page: isRequestedContextActive ? pageNumber : prev.page,
          totalPages: isRequestedContextActive ? totalPages : prev.totalPages,
          availableContexts: nextAvailableContexts,
          contextChatSummaries: {
            ...(prev.contextChatSummaries || {}),
            ...responseContextSummaries,
          },
          contextHistories: {
            ...(prev.contextHistories || {}),
            [requestedContext]: merged,
          },
          contextPagination: {
            ...(prev.contextPagination || {}),
            [requestedContext]: { page: pageNumber, totalPages },
          },
        };
      });

      updateHistorySession({ loading: false });
      setLoadingHistory(false);

      if (shouldPreservePrependScroll) {
        restorePrependScrollAnchor(prependScrollAnchor);
      } else if (shouldOpenAtTop) {
        setTimeout(() => scrollToTop(true), 60);
      } else if (pageNumber === 1) {
        setTimeout(() => scrollToBottom(false, true), 60);
      }
      if (!preserveStreamWait) {
        setHistoryStreamWait(false);
      }
      if (pageNumber === 1 && reentryHistoryHydrating) {
        setReentryHistoryHydrating(false);
      }
    } catch (err) {
      console.error("Failed to load previous predictions", err);
      updateHistorySession({
        loading: false,
        errorMessage: getReadableError(err, "Unable to load previous predictions.")
      });
      setLoadingHistory(false);
      if (!preserveStreamWait) {
        setHistoryStreamWait(false);
      }
      setHistoryError(true);
      if (pageNumber === 1 && reentryHistoryHydrating) {
        setReentryHistoryHydrating(false);
      }
    } finally {
      if (shouldPreservePrependScroll) {
        pagingRequestInFlightRef.current = false;
      }
    }
  };


  /* ------------------------------------------------------------------
   * CONTINUE EVALUATION
   * ------------------------------------------------------------------ */

  const handleEvaluateContinue = async (promptAction) => {
    if (promptAction === "RESET" && (busy || resettingContext)) return;
    if (promptAction === "STOP" && stopRequestInFlightRef.current) return;

    setIsMultiline(false);
    const textGenerationPromptStep =
      promptAction === "prompt" ? getTextGenerationPromptStep() : null;
    if (textGenerationPromptStep) {
      highlightSection(textGenerationPromptStep.ref);
      showInfoPopover(
        textGenerationPromptStep.targetId,
        textGenerationPromptStep.message,
        2500
      );
      return;
    }

    if (promptAction === "prompt" && isFlorence && selectedImages.length === 0) {
      highlightSection(uploadRef);
      showInfoPopover("llm-upload-step", "Please upload an image for Florence2", 2500);
      return;
    }

    if (promptAction === "prompt" && tokensExceeded) {
      showInfoPopover("llm-prompt-step", tokensShortMsg, 3000);
      return;
    }
    if (promptAction === "prompt" && isLocked) {
      return;
    }
    if (!eval_id) {
      showInfoPopover("llm-prompt-step", "Evaluation is not ready yet. Please try again in a moment.", 2500);
      return;
    }

    if (promptAction === "STOP") {
      stopRequestInFlightRef.current = true;
      setStoppingEvaluation(true);
    }

    if (promptAction === "prompt") {
      const scrollElement = getScrollElement();
      autoScrollRef.current = true;
      lastScrollTopRef.current = scrollElement?.scrollTop || 0;
      setBusy(true);
      if (window._ora) {
        window._ora.push([
          "trackEvent",
          {
            type: "button_click",
            name: "Continued the same Evalaution",
            component: "Evaluation Page",
          }
        ]);
      }

    } else if (promptAction === "STOP") {
      setBusy(false);
      setEvalBusyPrompt(eval_id, false);
      // setBusy(false);
      if (window._ora) {
        window._ora.push([
          "trackEvent",
          {
            type: "button_click",
            name: "Completed Evaluation",
            component: "Evaluation Page",
          }
        ]);
      }

    }

    if (promptAction === "RESET") {
      setResettingContext(true);

      try {
        await getDataFromApi(
          `${window.ENV.API_URL}/evaluations/action?visual_inspection=false`,
          "POST",
          { _id: eval_id, action: "reset" }
        );
        update({ errorMessage: "" }, eval_id);
        showInfoPopover("llm-prompt-step", "Model context has been reset.", 2500);
      } catch (err) {
        const errorMessage = getReadableError(err, "Unable to reset the model context. Please try again.");
        update({ errorMessage }, eval_id);
        showInfoPopover("llm-prompt-step", errorMessage, 3500);
      } finally {
        setResettingContext(false);
      }

      return;
    }




    
    const chatImages = normalizeMediaItems(session.chatImages || []);
    const currentImages = normalizeMediaItems(
      session.selectedImages || [],
      getReservedImageNameSet(chatImages)
    ).slice(
      0,
      isVisualQuestionAnswering
        ? (isFlorence ? 1 : MAX_VQA_IMAGES)
        : Number.POSITIVE_INFINITY
    );
    const submittedPrompt = promptText.trim();
    const submittedTurnId = promptAction === "prompt" ? Date.now() : null;

    if (currentImages.some(isVideoMedia)) {
      showInfoPopover("llm-upload-step", "Video input is not supported right now.", 3000);
      return;
    }
    if (promptAction === "prompt") {
      setSessionStreamWait(false, eval_id);
      applySessionUpdate(prev => ({
        ...prev,
        responseStreaming: true,
        streamingContext: prev.activeContext || 1,
        errorMessage: "",
      }), eval_id);
      update({
        loading: true,
        pendingPrompt: submittedPrompt,
      }, eval_id);

      applySessionUpdate(prev => {
        const history = Array.isArray(prev.history) ? [...prev.history] : [];
        const hasEntry = history.some((h) => h.turnId === submittedTurnId);

        if (!hasEntry && (submittedPrompt || currentImages.length > 0)) {
          history.push({
            turnId: submittedTurnId,
            prompt: submittedPrompt,
            response: STREAM_STATUS.ANALYSING,
            ...(currentImages.length > 0
              ? {
                  local_images: currentImages.map((img) => ({
                    name: img.name,
                    type: img.type || "image",
                    base64: img.base64 || null,
                    url: img.url || img.base64 || null,
                    thumbUrl: img.thumbUrl || null,
                  }))
                }
              : {})
          });
        }

        return { ...prev, history };
      }, eval_id);
    }



    
    setPromptText("");

    setTimeout(scrollToBottom, 200);

    try {
      let generatedZipFile = null;

      /* ------------------------------------------------------------------
       *  CASE 1 → image-capable tasks
       * ------------------------------------------------------------------ */

      if (supportsImage) {

        const hasImage = currentImages.length > 0;
        const hasPrompt = submittedPrompt.length > 0;
        let chatImageObj = null;
        let imageNameToSend = "none";
        const chatImageObjs = [...currentImages];
        
        if (hasImage) {

          
          if (isVisualQuestionAnswering) {
            imageNameToSend = chatImageObjs[0]?.name || "none";

            update({
              chatImages: [...chatImages, ...chatImageObjs]
            }, eval_id);

          }

          for (let i = 0; i < currentImages.length; i++) {
            const img = currentImages[i];
            const isFirst = i === 0;
            const isLast = i === currentImages.length - 1;
            const sanitizedImageName = sanitizeImageName(img.name);

            let payload = {
              _id: eval_id,
              action: "data_upload",
              image_name: sanitizedImageName,
              input_prompt: submittedPrompt || "",
              isFirst,
              iscomplete: isLast,
              task_type: taskType,
              model_id: data?.info?._id,
              ...(isZeroShotTask
                ? { image_url: await uploadLongClip(img) }
                : { image: img.base64.split(",")[1] } // VQA: base64
              ),
              total_images: currentImages?.length
            };

            if (hasPrompt) payload.prompt = submittedPrompt;


            await getDataFromApi(
              `${window.ENV.API_URL}/evaluations/action?visual_inspection=false`,
              "POST",
              payload
            );
          }
        }

        /* ------------------------------------------------------
         * 2. User gives ONLY prompt (no image)
         * ------------------------------------------------------ */
        if (!hasImage && hasPrompt) {
          const payload = {
            _id: eval_id,
            action: "prompt",
            input_prompt: submittedPrompt,
          };

          await getDataFromApi(
            `${window.ENV.API_URL}/evaluations/action?visual_inspection=false`,
            "POST",
            payload
          );

          imageNameToSend = "none";  
        }

        /* ------------------------------------------------------
         * 3. STOP Evaluation
         * ------------------------------------------------------ */
        if (promptAction === "STOP") {
          const payload = { _id: eval_id, action: "stop" };


          await getDataFromApi(
            `${window.ENV.API_URL}/evaluations/action?visual_inspection=false`,
            "POST",
            payload
          );
          closeRegisteredStream(eval_id);
          setBusy(false);
          setEvalBusyPrompt(eval_id, false);

          applySessionUpdate(prev => {
            const history = Array.isArray(prev.history) ? [...prev.history] : [];
            const last = history[history.length - 1];
            if (last?.response === STREAM_STATUS.ANALYSING) {
              last.response = NO_RESPONSE_MESSAGE;
            }
            return {
              ...prev,
              history,
              loading: false,
              pendingPrompt: "",
              responseStreaming: false
            };
          }, eval_id);
          await refreshContextMetadata(eval_id, activeContext);
          return;
        }

        /* ------------------------------------------------------
         * 4. Call Predictions (SSE stream)
         * ------------------------------------------------------ */
        update({ firstEvaluation: false }, eval_id);
        handlepredictions(
          eval_id,
          submittedPrompt,
          null,
          imageNameToSend,
          chatImageObjs,
          "user",
          submittedTurnId
        );

        update({ selectedImages: [] }, eval_id);
        return;
      }

      /* ------------------------------------------------------------------
       *  CASE 2 → other LLM/VLM models (no images allowed)
       * ------------------------------------------------------------------ */
      const payload =
        promptAction === "STOP"
          ? { _id: eval_id, action: "stop" }
          : {
            _id: eval_id,
            action: promptAction,
            input_prompt: submittedPrompt
          };


      await getDataFromApi(
        `${window.ENV.API_URL}/evaluations/action?visual_inspection=false`,
        "POST",
        payload
      );

      if (promptAction !== "STOP") {
        update({ firstEvaluation: false }, eval_id);
        handlepredictions(
          eval_id,
          submittedPrompt,
          null,
          null,
          [],
          "user",
          submittedTurnId
        );
      } else {
        closeRegisteredStream(eval_id);
        setBusy(false);
        setEvalBusyPrompt(eval_id, false);
        applySessionUpdate(prev => ({
          ...prev,
          loading: false,
          pendingPrompt: "",
          responseStreaming: false,
        }), eval_id);
        await refreshContextMetadata(eval_id, activeContext);
      }

      update({ selectedImages: [] }, eval_id);
    } catch (err) {
      console.error("Evaluation failed", err);
      if (promptAction === "STOP") {
        stopRequestInFlightRef.current = false;
        setStoppingEvaluation(false);
      }
      const errorMessage = getReadableError(err, "Evaluation request failed. Please retry.");
      applySessionUpdate(prev => {
        const history = Array.isArray(prev.history) ? [...prev.history] : [];
        const lastIndex = history.length - 1;
        if (lastIndex >= 0 && history[lastIndex]?.response === STREAM_STATUS.ANALYSING) {
          history[lastIndex] = {
            ...history[lastIndex],
            response: NO_RESPONSE_MESSAGE,
            retry: true,
            errorMessage,
          };
        }
        return {
          ...prev,
          history,
          loading: false,
          pendingPrompt: "",
          responseStreaming: false,
          errorMessage,
        };
      }, eval_id);
      setBusy(false);
      setEvalBusyPrompt(eval_id, false);
    } finally {
      update({ loading: false }, eval_id);
    }
  };

  async function uploadLongClip(image) {
    const file = base64ToFile(image.base64, sanitizeImageName(image.name));
    const key = await uploadToS3(file, eval_id);
    return `https://de-cloudfront-s3.s3.us-east-2.amazonaws.com/${key}`;
  }


  function base64ToFile(base64String, fileName, mimeType) {
    const safeFileName = sanitizeImageName(fileName);
    const byteString = atob(base64String.split(",")[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    let finalMime = mimeType;
    if (!mimeType) {
      finalMime = getMimeType(safeFileName);
    }

    return new File([ab], safeFileName, { type: finalMime });
  }

  const uploadToS3 = async (file, evalId) => {
    if (!file || !evalId) {
      throw new Error("Missing file or evalId");
    }

    const {
      aws_cf_access_key,
      aws_cf_secret_key,
      aws_cf_bucket_name,
    } = data?.cloudfront_details || {};

    if (!aws_cf_access_key || !aws_cf_secret_key || !aws_cf_bucket_name) {
      throw new Error("Missing S3 credentials or bucket name");
    }

    update({ uploadComplete: false, uploadProgress: 0 }, evalId);

    const s3Client = new S3Client({
      region: REGION,
      credentials: {
        accessKeyId: aws_cf_access_key,
        secretAccessKey: aws_cf_secret_key,
      },
    });

    const safeFileName = sanitizeImageName(file.name);
    const safeFile = renameFile(file, safeFileName);
    const keyName = `evaluations/${evalId}/${safeFileName}`;

    const uploader = new Upload({
      client: s3Client,
      params: {
        Bucket: aws_cf_bucket_name,
        Key: keyName,
        Body: safeFile,
        ContentType: safeFile.type || "application/octet-stream",
      },
      leavePartsOnError: false,
    });

    uploader.on("httpUploadProgress", (p) => {
      if (p?.loaded) {
        const total = p.total ?? p.loaded;
        const pct = Math.round((p.loaded / total) * 100);
        update({ uploadProgress: pct }, evalId);
      }
    });

    await uploader.done();
    update({ uploadComplete: true }, evalId);

    return keyName;
  };


  /* ------------------------------------------------------------------
   * LOAD PREVIOUS HISTORY
   * ------------------------------------------------------------------ */
  useEffect(() => {
    if (!eval_id) return;
    if (!chatBox) return;




    
    if (session.firstEvaluation && chatBox && eval_id === chatBoxPromptid) {
      const pendingInitialPrediction =
        session.pendingPredictionsByEvalId?.[eval_id];
      const initialPrompt =
        String(chatBoxPrompt || "").trim()
          ? chatBoxPrompt
          : pendingInitialPrediction?.prompt || "";
      const initialImages =
        Array.isArray(chatImag) && chatImag.length > 0
          ? chatImag
          : pendingInitialPrediction?.chatImages || [];

      update({ pendingPrompt: initialPrompt });
      // console.log('eval ids', eval_id)
      if (isVisualQuestionAnswering) {
        handlepredictions(
          eval_id,
          initialPrompt,
          initialImages?.[0],
          initialImages?.[0]?.name,
          initialImages,
          "initial"
        );
      } else if (isZeroShotTask) {
        handlepredictions(
          eval_id,
          initialPrompt,
          null,
          null,
          initialImages,
          "initial"
        );
      } else {

        handlepredictions(
          eval_id,
          initialPrompt,
          null,
          null,
          [],
          "initial"
        );
      }
    }
  }, [
    eval_id,
    chatBox,
    chatBoxPrompt,
    chatBoxPromptid,
    chatImag,
    session.firstEvaluation,
    session.pendingPredictionsByEvalId,
  ]);



  const autoStartedEvalRef = useRef(new Set());

  // const [streamWait, setStreamWait] = useState(false)
  useEffect(() => {
    if (!eval_id) return;
    const currentStatus = evaluationStatusMap?.status || message;
    const hasInitialSubmittedPrompt =
      eval_id === chatBoxPromptid && Boolean(String(chatBoxPrompt || "").trim());

    if (
      reentryHistoryHydrating &&
      isEvaluating &&
      !historyRequestedContextsRef.current.has(activeContext)
    ) {
      handleprevpredictions(eval_id, 1, {
        context: activeContext,
        initialScroll: "bottom",
        preserveStreamWait: true,
        preserveStreaming: true,
      });
    }

    if (currentStatus === "Queued" || (session?.firstEvaluation && currentStatus === "Evaluating")) {

      if (
        currentStatus === "Evaluating" &&
        (chatImag?.length || 0) === 0 &&
        !hasInitialSubmittedPrompt &&
        !isTextGeneration
      ) {
        autoStartedEvalRef.current.add(eval_id);
        handlepredictions(eval_id,
          null,
          null,
          null,
          [],
          "auto")
      }
      if (currentStatus === "Evaluating" && chatBoxPrompt === "" && isTextGeneration) {
        autoStartedEvalRef.current.add(eval_id);
        handlepredictions(eval_id,
          null,
          null,
          null,
          [],
          "auto")
      }
      return;
    }
    
    if (isFinishedEvaluation) {
      const cachedActiveContextHistory =
        session.contextHistories?.[activeContext];
      const hasCachedActiveContextHistory =
        session.history?.length > 0 ||
        (Array.isArray(cachedActiveContextHistory) &&
          cachedActiveContextHistory.length > 0);
      const isLocallyStartedEvaluation = eval_id === chatBoxPromptid;

      if (
        !isLocallyStartedEvaluation &&
        !hasCachedActiveContextHistory &&
        !historyRequestedContextsRef.current.has(activeContext)
      ) {
        handleprevpredictions(eval_id, 1, {
          context: activeContext,
          initialScroll: "bottom",
        });
      }
      setChatBoxPrompt?.("");
      return;
    }

    if (session.history?.length > 0) return;
  }, [
    eval_id,
    evaluationStatusMap?.status,
    message,
    session?.firstEvaluation,
    session.history?.length,
    session.contextHistories,
    chatImag?.length,
    chatBoxPrompt,
    chatBoxPromptid,
    isTextGeneration,
    activeContext,
    isFinishedEvaluation,
    isEvaluating,
    reentryHistoryHydrating,
  ]);


  const handleScroll = useCallback(() => {
    const el = getScrollElement();
    if (!el) return;

    const currentScrollTop = el.scrollTop;
    const distanceToBottom =
      el.scrollHeight - currentScrollTop - el.clientHeight;
    const isScrollingUp = currentScrollTop < lastScrollTopRef.current - 1;
    const topPagingSuppressed = Date.now() < topPagingSuppressedUntilRef.current;
    const isProgrammaticInitialTopScroll =
      topPagingSuppressed && autoScrollRef.current;

    if (!isProgrammaticInitialTopScroll) {
      if (isScrollingUp) {
        autoScrollRef.current = false;
      } else if (distanceToBottom <= BOTTOM_THRESHOLD) {
        autoScrollRef.current = true;
      }
    }
    lastScrollTopRef.current = currentScrollTop;

    if (
      currentScrollTop <= 0 &&
      !autoScrollRef.current &&
      !topPagingSuppressed &&
      !loading &&
      session.page < session.totalPages
    ) {
      handleprevpredictions(eval_id, session.page + 1, {
        context: activeContext,
      });
    }
  }, [
    activeContext,
    getScrollElement,
    loading,
    session.page,
    session.totalPages,
    eval_id,
  ]);

  // console.log("selectedimages", selectedImages);

  const preprocessResponse = (text) => {
    return text
      ?.replace(/\\n\\n/g, "__PARA_BREAK__")
      .replace(/\\n/g, "\n")
      .replace(/__PARA_BREAK__/g, "\n\n")
      .replace(/\\"/g, '"');
  };

  const parseStreamingMarkdown = (text) => {
    let result = "";
    let inBold = false;

    for (let i = 0; i < text.length; i++) {
      if (text[i] === "*" && text[i + 1] === "*") {
        inBold = !inBold;
        result += inBold ? "<strong>" : "</strong>";
        i++;
      } else {
        result += text[i];
      }
    }
    return result;
  };

  const handleEnterSubmit = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (isLocked) return;

      if (tokensExceeded) {
        showInfoPopover("llm-prompt-step", tokensShortMsg, 3000);
        return;
      }

      handleEvaluateContinue("prompt");
    }
  };


  const handleEnterSubmitstart = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();

       const longclipError = getLongclipPromptError(promptText);
      if (longclipError) {
        showInfoPopover("llm-prompt-step", longclipError, 3000);
        return;
      }



      if (tokensExceeded) {
        showInfoPopover("llm-prompt-step", tokensShortMsg, 3000);
        return;
      }

      attemptStartEvaluation();
    }
  };


  const hasRetryPending = session.history?.some(
    h => h.retry === true
  );


  const isBusy = loading || responseStreaming || Creating;
  const isLocked = responseStreaming || busy || Creating || isEvalBusyPrompt || hasRetryPending;
  const evaluationActionDisabled = busy || resettingContext;

  //  --------------- textarea height ----------- //

  const [isMultiline, setIsMultiline] = useState(false);
  const PROMPT_TEXTAREA_MIN_HEIGHT = chatBox ? 40 : 72;
  const PROMPT_TEXTAREA_MAX_HEIGHT = chatBox ? 150 : 160;

  const syncPromptTextareaHeight = useCallback((el = promptTextareaRef.current) => {
    if (!el) return;

    el.style.height = `${PROMPT_TEXTAREA_MIN_HEIGHT}px`;
    const nextHeight = Math.min(
      Math.max(el.scrollHeight, PROMPT_TEXTAREA_MIN_HEIGHT),
      PROMPT_TEXTAREA_MAX_HEIGHT
    );
    el.style.height = `${nextHeight}px`;
    setIsMultiline(nextHeight > PROMPT_TEXTAREA_MIN_HEIGHT);
  }, [PROMPT_TEXTAREA_MAX_HEIGHT, PROMPT_TEXTAREA_MIN_HEIGHT]);

  useEffect(() => {
    syncPromptTextareaHeight();
  }, [promptText, syncPromptTextareaHeight]);

  const tokenizeLongclipClasses = useCallback((text) => {
    return String(text || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  }, []);

  const setLongclipClassesAndSyncPrompt = useCallback((nextClasses) => {
    const cleaned = (nextClasses || []).map((t) => String(t).trim()).filter(Boolean);
    setLongclipClassNames(cleaned);
    setPromptText(cleaned.join(","));
  }, []);

  useEffect(() => {
    if (!isZeroShotClassification) return;
    setIsMultiline(false);

    const initial = (promptText || "").trim();
    if (!initial) {
      setLongclipClassNames([]);
      setLongclipClassDraft("");
      return;
    }

    const parsed = initial
      .split(",")
      .map((p) => p.trim())
      .filter(Boolean);
    setLongclipClassNames(parsed);
    setLongclipClassDraft("");
    const normalized = parsed.join(",");
    if (initial !== normalized) setPromptText(normalized);
  }, [isZeroShotClassification, promptText]);

  const commitLongclipDraft = useCallback(() => {
    const tokens = tokenizeLongclipClasses(longclipClassDraft);
    if (!tokens.length) return longclipClassNames;

    const next = [...(longclipClassNames || []), ...tokens];
    setLongclipClassDraft("");
    setLongclipClassesAndSyncPrompt(next);
    return next;
  }, [
    longclipClassDraft,
    longclipClassNames,
    setLongclipClassesAndSyncPrompt,
    tokenizeLongclipClasses
  ]);

  const removeLongclipClassAt = useCallback((idx) => {
    const next = (longclipClassNames || []).filter((_, i) => i !== idx);
    setLongclipClassesAndSyncPrompt(next);
  }, [longclipClassNames, setLongclipClassesAndSyncPrompt]);

  const handlePromptSuggestionClick = (suggestion) => {
    if (!suggestion || loadButton || Creating || isLocked) return;

    if (isZeroShotClassification) {
      setLongclipClassesAndSyncPrompt(tokenizeLongclipClasses(suggestion));
      setLongclipClassDraft("");
    } else {
      setPromptText(suggestion);
    }
  };

  const clearTextExamplePreview = useCallback(() => {
    if (!textPreviewExampleIdRef.current) return;

    textPreviewExampleIdRef.current = "";
    setHoveredTextExampleId("");

    const committedExample = evaluationExamples.find(
      (item) => item.id === activeExampleIdRef.current
    );
    if (committedExample?.prompt) {
      setPromptText(committedExample.prompt);
    } else {
      setPromptText(promptBeforeTextPreviewRef.current);
    }
    promptBeforeTextPreviewRef.current = "";
  }, [evaluationExamples]);

  const handleTextExampleMouseEnter = useCallback(
    (example) => {
      if (!example?.prompt || loadButton || Creating || isLocked) return;
      if (textPreviewExampleIdRef.current === example.id) return;

      if (!textPreviewExampleIdRef.current) {
        const committedExample = evaluationExamples.find(
          (item) => item.id === activeExampleIdRef.current
        );
        promptBeforeTextPreviewRef.current =
          committedExample?.prompt ?? promptTextRef.current;
      }

      textPreviewExampleIdRef.current = example.id;
      setHoveredTextExampleId(example.id);
      setPromptText(example.prompt);
    },
    [Creating, evaluationExamples, isLocked, loadButton]
  );

  const handleTextExampleMouseLeave = useCallback(
    (example, event) => {
      if (textPreviewExampleIdRef.current !== example.id) return;

      const nextTarget = event?.relatedTarget;
      if (
        nextTarget instanceof Element &&
        nextTarget.closest(".llm-example-prompt-badge")
      ) {
        return;
      }

      clearTextExamplePreview();
    },
    [clearTextExamplePreview]
  );

  const handleExampleSelect = async (example) => {
    if (!example || loadButton || Creating || isLocked) return;
    if (example.image && suggestedImageSelectionInFlightRef.current) return;

    const isImageOnlyExample = example.modality === "image";
    const allExampleImages = example.image
      ? (Array.isArray(example.images) && example.images.length
          ? example.images
          : [example.image])
      : [];
    const selectedImageNames = new Set(selectedImages.map((image) => image?.name));

    if (
      isImageOnlyExample &&
      allExampleImages.every((image) => selectedImageNames.has(image.name))
    ) {
      const namesToRemove = new Set(allExampleImages.map((image) => image.name));
      update((current) => ({
        selectedImages: (current.selectedImages || []).filter(
          (image) => !namesToRemove.has(image?.name)
        ),
      }));
      setPromptText("");
      setActiveExample("");
      return;
    }

    if (!isImageOnlyExample && activeExampleIdRef.current === example.id) return;

    if (!example.image) {
      textPreviewExampleIdRef.current = "";
      setHoveredTextExampleId("");
      promptBeforeTextPreviewRef.current = "";
    }

    setActiveExample(example.id);
    if (example.prompt) {
      handlePromptSuggestionClick(example.prompt);
    } else {
      setPromptText("");
    }

    if (!example.image) {
      update({ selectedImages: [] });
      return;
    }

    const maxExampleImages = isVisualQuestionAnswering
      ? (isFlorence ? 1 : MAX_VQA_IMAGES)
      : Number.POSITIVE_INFINITY;
    const availableSlots = Math.max(0, maxExampleImages - selectedImages.length);
    const exampleImages = isImageOnlyExample
      ? allExampleImages
          .filter((image) => !selectedImageNames.has(image.name))
          .slice(0, availableSlots)
      : allExampleImages.slice(0, maxExampleImages);

    if (!exampleImages.length) {
      showInfoPopover(
        "llm-upload-step",
        isFlorence
          ? "Florence2 supports one image at a time."
          : `You can select up to ${MAX_VQA_IMAGES} images.`,
        3000
      );
      return;
    }

    const placeholderImages = exampleImages.map((image) => ({
        name: image.name,
        type: "image",
        thumbUrl: image.src,
        url: image.src,
      }));

    if (isImageOnlyExample) {
      update((current) => ({
        selectedImages: [
          ...(current.selectedImages || []),
          ...placeholderImages,
        ],
      }));
    } else {
      update({ selectedImages: placeholderImages });
    }

    suggestedImageSelectionInFlightRef.current = true;
    setSelectingSuggestedImage(example.id);

    try {
      const selectedExampleImages = await Promise.all(
        exampleImages.map((image) => loadSuggestedImage(image))
      );

      if (isImageOnlyExample) {
        const loadedNames = new Set(exampleImages.map((image) => image.name));
        update((current) => ({
          selectedImages: [
            ...(current.selectedImages || []).filter(
              (image) => !loadedNames.has(image?.name)
            ),
            ...selectedExampleImages,
          ],
        }));
      } else if (activeExampleIdRef.current === example.id) {
        update({ selectedImages: selectedExampleImages });
      }
    } catch (error) {
      console.error("Failed to select example image", error);
      if (isImageOnlyExample) {
        const failedNames = new Set(exampleImages.map((image) => image.name));
        update((current) => ({
          selectedImages: (current.selectedImages || []).filter(
            (image) => !failedNames.has(image?.name)
          ),
        }));
      } else if (activeExampleIdRef.current === example.id) {
        update({ selectedImages: [] });
      }
      showInfoPopover(
        "llm-upload-step",
        "Unable to select these images. Please try another example.",
        3000
      );
    } finally {
      suggestedImageSelectionInFlightRef.current = false;
      setSelectingSuggestedImage("");
    }
  };

  const handleStartRunClick = () => {
    const compiledPrompt = isZeroShotClassification
      ? (commitLongclipDraft() || []).join(",")
      : promptText;

    const longclipError = getLongclipPromptError(compiledPrompt);
    if (longclipError) {
      showInfoPopover("llm-prompt-step", longclipError, 3000);
      return;
    }
    if (tokensExceeded) {
      showInfoPopover("llm-prompt-step", tokensShortMsg, 3000);
      return;
    }
    attemptStartEvaluation(
      isZeroShotClassification ? compiledPrompt : undefined
    );
  };

  const handleChange = (e) => {

    const el = e.target;
    setPromptText(el.value);
    if (textPreviewExampleIdRef.current) {
      textPreviewExampleIdRef.current = "";
      setHoveredTextExampleId("");
      promptBeforeTextPreviewRef.current = "";
    }
    setActiveExampleId((currentId) => {
      const example = evaluationExamples.find((item) => item.id === currentId);
      if (!example) return "";
      const nextId = example.prompt === el.value ? currentId : "";
      activeExampleIdRef.current = nextId;
      return nextId;
    });

    syncPromptTextareaHeight(el);
  };


  const hasInput =
    promptText.trim().length > 0 ||
    (supportsImage && session.selectedImages?.length > 0);

  const promptInputScrollStyle = isZeroShotTask
    ? {
      maxHeight: "132px",
      overflowY: "auto",
      overflowX: "hidden",
      paddingRight: "4px",
      scrollbarWidth: "thin"
    }
    : {};

  const selectedMediaScrollStyle = isZeroShotTask
    ? {
      maxHeight: "126px",
      overflowY: "auto",
      overflowX: "hidden",
      paddingTop: "5px",
      paddingRight: "4px",
      scrollbarWidth: "thin"
    }
    : {};

  const composerPlaceholder = isZeroShotClassification
    ? "Upload images and enter comma separated prompt to start evaluation eg: cat,dog,bird"
    : isTextGeneration
      ? (chatBox ? "Enter a prompt to continue evaluation..." : "Message this model...")
      : isFlorence
        ? (chatBox
          ? "Upload an image to continue evaluation; prompt is optional..."
          : "Upload an image, or add an optional prompt...")
        : isVisualQuestionAnswering
          ? (chatBox
            ? "Enter a prompt or upload image to continue evaluation..."
            : "Ask anything, or upload an image...")
          : "Upload an image and enter a prompt to start evaluation";

  const renderSelectedImageThumbs = () => {
    if (!selectedImages.length) return null;

    return (
      <div className="d-flex gap-2 flex-wrap mb-2" style={selectedMediaScrollStyle}>
        {selectedImages.map((img, idx) => (
          <div key={`${img.name || "image"}-${idx}`} style={{ position: "relative" }}>
            <img
              src={img.thumbUrl || img.url || img.base64}
              alt={img.name || "selected image"}
              loading="lazy"
              decoding="async"
              style={{ width: "60px", height: "50px", borderRadius: "8px", objectFit: "contain" }}
            />
            <FontAwesomeIcon
              icon={faTimesCircle}
              onClick={() => removeImage(idx)}
              style={{
                position: "absolute",
                top: "-5px",
                right: "-5px",
                cursor: "pointer",
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderTryExamples = () => {
    if (!evaluationExamples.length) return null;

    const hasMultiImageCombinedExample = evaluationExamples.some(
      (example) =>
        example.modality === "text+image" &&
        Array.isArray(example.images) &&
        example.images.length > 1
    );
    const availableModalities = EXAMPLE_MODALITY_OPTIONS
      .filter((option) =>
        evaluationExamples.some((example) => example.modality === option.value)
      )
      .map((option) =>
        option.value === "text+image"
          ? {
              ...option,
              label: hasMultiImageCombinedExample
                ? "images + text prompt"
                : "image + text prompt",
              heading: hasMultiImageCombinedExample
                ? "Try with these sample Images + Text prompt examples"
                : "Try with these sample Image + Text prompt examples",
            }
          : option
      );
    const examplesDisabled =
      loadButton || Creating || isLocked || Boolean(selectingSuggestedImage);
    const activeOption =
      availableModalities.find((option) => option.value === activeExampleModality) ||
      availableModalities[0];
    const exampleRowRef = exampleGalleryRowRef;

    const selectExampleModality = (modality) => {
      if (!modality || modality === activeExampleModalityRef.current) {
        setExamplePhase("enter");
        return;
      }
      activeExampleModalityRef.current = modality;
      setActiveExampleModality(modality);
      setExamplePhase("enter");
    };

    const scrollExampleRow = (direction) => {
      const row = exampleRowRef.current;
      if (!row) return;
      row.scrollBy({
        left: direction * Math.max(180, Math.floor(row.clientWidth * 0.7)),
        behavior: "smooth",
      });
    };

    const handleExampleRowWheel = (event) => {
      const row = event.currentTarget;
      if (row.scrollWidth <= row.clientWidth) return;

      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
        ? event.deltaX
        : event.deltaY;
      const canMoveLeft = delta < 0 && row.scrollLeft > 0;
      const canMoveRight =
        delta > 0 && row.scrollLeft + row.clientWidth < row.scrollWidth - 1;

      if (!canMoveLeft && !canMoveRight) return;
      event.preventDefault();
      row.scrollLeft += delta;
    };

    const renderExample = (example) => {
      if (!example.image) {
        return (
          <button
            key={example.id}
            type="button"
            className={`llm-example-prompt-badge ${
              activeExampleId === example.id ? "selected" : ""
            } ${hoveredTextExampleId === example.id ? "is-previewing" : ""}`}
            onClick={() => handleExampleSelect(example)}
            onMouseEnter={() => handleTextExampleMouseEnter(example)}
            onMouseLeave={(event) => handleTextExampleMouseLeave(example, event)}
            disabled={examplesDisabled}
            aria-label={example.prompt}
            aria-pressed={activeExampleId === example.id}
          >
            <span className="llm-example-prompt-badge-text">{example.prompt}</span>
          </button>
        );
      }

      const exampleImages =
        Array.isArray(example.images) && example.images.length
          ? example.images
          : [example.image];
      const isSelected = example.modality === "image"
        ? exampleImages.every((sampleImage) =>
            selectedImages.some((image) => image?.name === sampleImage.name)
          )
        : activeExampleId === example.id;
      const isCombo = example.modality === "text+image";

      return (
        <button
          key={example.id}
          type="button"
          className={`llm-example-card ${
            exampleImages.length > 1 ? "llm-example-card-multi" : ""
          } ${isCombo ? "llm-example-card-combo" : "llm-example-card-image"} ${
            isSelected ? "selected" : ""
          }`}
          onClick={() => handleExampleSelect(example)}
          disabled={examplesDisabled}
          title={example.prompt || example.label}
          aria-pressed={isSelected}
        >
          {exampleImages.length > 1 ? (
            <div className="llm-example-card-images">
              {exampleImages.map((image) => (
                <img
                  key={image.name}
                  src={image.src}
                  alt={image.label}
                  loading="eager"
                  decoding="async"
                />
              ))}
            </div>
          ) : (
            <img
              src={example.image.src}
              alt={example.label}
              loading="eager"
              decoding="async"
            />
          )}
          {isCombo && example.prompt && (
            <span className="llm-example-card-label">{example.prompt}</span>
          )}
        </button>
      );
    };

    if (!activeOption) return null;

    const activeExamples = evaluationExamples.filter(
      (example) => example.modality === activeOption.value
    );
    const showRowNav =
      activeOption.value !== "text" && activeExamples.length > 3;

    return (
      <div
        className="llm-example-gallery llm-example-gallery-offset"
        aria-label="Try examples"
        onMouseEnter={() => setIsExampleGalleryHovered(true)}
        onMouseLeave={(event) => {
          setIsExampleGalleryHovered(false);
          const nextTarget = event.relatedTarget;
          if (
            nextTarget instanceof Element &&
            nextTarget.closest(".llm-example-prompt-badge")
          ) {
            return;
          }
          clearTextExamplePreview();
        }}
        onFocusCapture={() => setIsExampleGalleryHovered(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsExampleGalleryHovered(false);
          }
        }}
      >
        <div className={`llm-example-gallery-slide is-${examplePhase}`}>
          <div className="llm-example-gallery-header">
            <span className="llm-example-gallery-title">
              <FontAwesomeIcon icon={faWandMagicSparkles} />
              <span className="llm-example-gallery-sentence">
                Try with these sample{" "}
                <strong>
                  {activeOption.value === "text"
                    ? "Text"
                    : activeOption.value === "image"
                      ? "Image"
                      : hasMultiImageCombinedExample
                        ? "Images + Text"
                        : "Image + Text"}
                </strong>{" "}
                {activeOption.value === "text+image" ? "prompt examples" : "examples"}.....
              </span>
            </span>

            <div className="llm-example-gallery-controls">
              {availableModalities.length > 1 && (
                <div
                  className="llm-example-gallery-dots"
                  role="tablist"
                  aria-label="Sample type"
                >
                  {availableModalities.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      role="tab"
                      aria-selected={option.value === activeOption.value}
                      aria-label={option.heading}
                      title={option.heading}
                      className={`llm-example-gallery-dot${
                        option.value === activeOption.value ? " is-active" : ""
                      }`}
                      onClick={() => selectExampleModality(option.value)}
                      disabled={examplesDisabled}
                    />
                  ))}
                </div>
              )}

            </div>
          </div>

          <div
            className={`llm-example-gallery-row-wrap${
              showRowNav ? " has-row-nav" : ""
            }${
              activeOption.value === "image"
                ? " llm-example-gallery-row-wrap--images"
                : ""
            }${
              activeOption.value === "text+image"
                ? " llm-example-gallery-row-wrap--combo"
                : ""
            }`}
          >
            {showRowNav && (
              <>
                <button
                  type="button"
                  className="llm-example-gallery-row-nav llm-example-gallery-row-nav--prev"
                  aria-label="Previous examples"
                  onClick={() => scrollExampleRow(-1)}
                  disabled={examplesDisabled}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>
                <button
                  type="button"
                  className="llm-example-gallery-row-nav llm-example-gallery-row-nav--next"
                  aria-label="Next examples"
                  onClick={() => scrollExampleRow(1)}
                  disabled={examplesDisabled}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </>
            )}

            <div
              ref={exampleRowRef}
              className={`llm-example-gallery-row ${
                activeOption.value === "text" ? "llm-example-prompt-list" : ""
              } ${
                activeOption.value === "image"
                  ? "llm-example-gallery-row--images"
                  : ""
              } ${
                activeOption.value === "text+image"
                  ? "llm-example-gallery-row--combo"
                  : ""
              }`}
              onWheel={handleExampleRowWheel}
              role="tabpanel"
            >
              {activeExamples.map(renderExample)}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const composerFormStyle = chatBox
    ? {
        border: "10px solid var(--bg-color7)",
        outline: "1px solid var(--leading-color)",
        outlineOffset: "-6px",
      }
    : undefined;

  const getPromptParts = (value) =>
    String(value || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

  const renderSubmittedPrompt = (value) => {
    const text = String(value || "").trim();
    if (!text) return null;

    const parts = getPromptParts(text);
    const shouldRenderParts = isZeroShotClassification && parts.length > 1;

    return (
      <div
        className="p-2 mt-2 rounded badge-border"
        title={text}
        style={{
          width: shouldRenderParts ? "100%" : "fit-content",
          maxWidth: "100%",
          minWidth: 0,
          maxHeight: "138px",
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin"
        }}
      >
        {shouldRenderParts ? (
          <div className="d-flex flex-wrap gap-1">
            {parts.map((part, idx) => (
              <span
                key={`${part}-${idx}`}
                className="px-2 py-1"
                style={{
                  border: "1px solid var(--glass-color)",
                  borderRadius: "999px",
                  fontSize: "12px",
                  lineHeight: 1.2,
                  maxWidth: "100%",
                  overflowWrap: "anywhere"
                }}
              >
                {part}
              </span>
            ))}
          </div>
        ) : (
          <span style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {text}
          </span>
        )}
      </div>
    );
  };

  const renderContextPanelToggleIcon = (direction = "collapse") => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="16" rx="3" stroke="currentColor" strokeWidth="1.7" />
      {direction === "collapse" ? (
        <path d="M9 4v16M15.5 9l-3 3 3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M9 4v16M12.5 9l3 3-3 3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </svg>
  );

  const renderUnifiedResultsToolbar = () => {
    if (!useGlobalScroll) return null;

    const hasToolbarActions = Boolean(toolbarActions);
    const hasContextControls = showContextNavigation;

    if (!hasToolbarActions && !hasContextControls) return null;

    return (
      <div
        className={`evaluation-results-llm-toolbar evaluation-results-llm-toolbar--unified${
          hasContextControls && contextPanelOpen
            ? " evaluation-results-llm-toolbar--panel-open"
            : " evaluation-results-llm-toolbar--panel-closed"
        }`}
      >
        <div className="evaluation-results-llm-toolbar__left">
          {hasContextControls && (
            contextPanelOpen ? (
              <>
                <span className="llm-context-panel-count llm-context-panel-count--toolbar-mode">
                  Context History
                </span>
                <StyledTooltip title="Hide contexts">
                  <button
                    type="button"
                    className="llm-context-panel-toggle llm-context-panel-toolbar-toggle"
                    onClick={() => setContextPanelOpen(false)}
                    aria-label="Hide contexts"
                    aria-expanded="true"
                  >
                    {renderContextPanelToggleIcon("collapse")}
                  </button>
                </StyledTooltip>
              </>
            ) : (
              <StyledTooltip title="Show contexts">
                <button
                  type="button"
                  className="llm-context-panel-reopen llm-context-panel-reopen--global"
                  onClick={() => setContextPanelOpen(true)}
                  aria-label="Show contexts"
                  aria-expanded="false"
                >
                  {renderContextPanelToggleIcon("expand")}
                </button>
              </StyledTooltip>
            )
          )}
        </div>

        {hasToolbarActions && (
          <div className="evaluation-results-llm-toolbar__right">
            <div className="evaluation-results-llm-toolbar__actions">
              {toolbarActions}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`llm-evaluation-field-shell ${
        showContextNavigation && contextPanelOpen
          ? "context-panel-open"
          : "context-panel-closed"
      }${useGlobalScroll ? " llm-evaluation-field-shell--global-scroll" : ""}`}
    >
      {renderUnifiedResultsToolbar()}

      <div
        className={`llm-results-scroll-host${useGlobalScroll ? " llm-results-scroll-host--active" : ""}`}
      >
      <div
        className={`llm-evaluation-field-body${useGlobalScroll ? " llm-evaluation-field-body--global-scroll" : ""}${useGlobalScroll && contextPanelOpen && showContextNavigation ? " context-panel-open-body" : ""}`}
      >
      {showContextNavigation && (
        <>
          <aside
            id="llm-context-tabs"
            className={`llm-context-panel ${contextPanelOpen ? "open" : "closed"}`}
            aria-label="Evaluation contexts"
            aria-hidden={!contextPanelOpen}
          >
            <div className={`llm-context-panel-header${useGlobalScroll ? " llm-context-panel-header--toolbar-mode" : ""}`}>
              {!useGlobalScroll && (
              <div>
                <div className="llm-context-panel-title">Context History</div>
                <div className="llm-context-panel-count">
                  {normalizedContexts.length} context{normalizedContexts.length === 1 ? "" : "s"}
                </div>
              </div>
              )}
              {!useGlobalScroll && (
              <StyledTooltip title="Hide contexts">
                <button
                  type="button"
                  className="llm-context-panel-toggle"
                  onClick={() => setContextPanelOpen(false)}
                  aria-label="Hide contexts"
                  aria-expanded="true"
                >
                  {renderContextPanelToggleIcon("collapse")}
                </button>
              </StyledTooltip>
              )}
            </div>

            {isEvaluating && (
              <StyledTooltip
                title={
                  isCreatingContext
                    ? "Creating a new context..."
                    : latestContextHasHistory
                    ? "Start a new context"
                    : "Send a prompt in the current context before creating another context"
                }
              >
                <span className="llm-context-panel-new-wrap">
                  <button
                    type="button"
                    className="llm-context-panel-new"
                    onClick={handleCreateContext}
                    disabled={
                      isCreatingContext ||
                      !latestContextHasHistory ||
                      evaluationActionDisabled ||
                      isLocked
                    }
                    aria-label={isCreatingContext ? "Creating a new context" : "Start a new context"}
                    aria-busy={isCreatingContext}
                  >
                    <span aria-hidden="true">+</span>
                    New context
                    {isCreatingContext && (
                      <span className="llm-context-panel-new-spinner" aria-hidden="true" />
                    )}
                  </button>
                </span>
              </StyledTooltip>
            )}

            <div className="llm-context-panel-list" role="tablist">
              {normalizedContexts.map((contextNumber) => {
                const isActive = activeContext === contextNumber;
                const contextTitle = getContextTitle(contextNumber);
                const contextHistory = isActive
                  ? history
                  : session.contextHistories?.[contextNumber];
                const hasContextActivity =
                  Array.isArray(contextHistory) && contextHistory.length > 0;
                const fallbackContextTitle = `Context ${contextNumber}`;
                const isContextTitleLoading = !contextTitle && hasContextActivity;
                const displayedContextTitle = contextTitle || fallbackContextTitle;
                const accessibleContextTitle = isContextTitleLoading
                  ? `Loading context ${contextNumber} title`
                  : displayedContextTitle;

                return (
                  <button
                    key={contextNumber}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-label={accessibleContextTitle}
                    aria-busy={isContextTitleLoading}
                    title={accessibleContextTitle}
                    className={`llm-context-panel-item ${isActive ? "active" : ""}`}
                    onClick={() => handleContextSelect(contextNumber)}
                  >
                    <span className="llm-context-panel-item-icon" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M7 18.5 3.5 21v-4.4A8 8 0 1 1 7 18.5Z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    <span className="llm-context-panel-item-copy">
                      {isContextTitleLoading ? (
                        <span className="llm-context-panel-title-loading">
                          <span className="llm-context-panel-title-spinner" aria-hidden="true" />
                          <span className="visually-hidden">{accessibleContextTitle}</span>
                        </span>
                      ) : (
                        <span>{displayedContextTitle}</span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          {!contextPanelOpen && !useGlobalScroll && (
            <StyledTooltip title="Show contexts">
              <button
                type="button"
                className="llm-context-panel-reopen"
                onClick={() => setContextPanelOpen(true)}
                aria-label="Show contexts"
                aria-expanded="false"
              >
                {renderContextPanelToggleIcon("expand")}
              </button>
            </StyledTooltip>
          )}
        </>
      )}

      <div
        ref={useGlobalScroll ? scrollHostRef : undefined}
        onScroll={useGlobalScroll ? handleScroll : undefined}
        className={`w-100 llm-context-content ${chatBox && !useGlobalScroll ? 'd-flex flex-column overflow-hidden flex-fill' : ''}${chatBox && useGlobalScroll ? ' llm-context-content--global-scroll' : ''}`}
        style={chatBox && !useGlobalScroll ? { minHeight: 0 } : undefined}
      >

        {/* ================================================================
          NEW EVALUATION FORM
      ================================================================= */}

        {!chatBox ? (
          <div className="w-100 de-width-chat chatbox-mb-pad ">
            <div ref={promptRef} id="llm-prompt-step">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleStartRunClick();
                }}
                className={`w-100 container-card p-2 p-sm-3 pb-1 pb-sm-1 ${
                  chatBox
                    ? "bg-transparent shadow"
                    : "llm-ai-composer bg-transparent"
                }`}
                style={composerFormStyle}
              >

                {renderSelectedImageThumbs()}

                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  accept="image/*,.zip"
                  onChange={(e) => {
                    const replace = replaceImagesOnSelectRef.current;
                    replaceImagesOnSelectRef.current = false;
                    setActiveExample("");
                    processFiles(e.target.files, { replace });
                  }}
                />

                <div className="d-flex mb-2 llm-composer-input-row">

                  {isZeroShotClassification ? (
                    <div
                      className="w-100 mb-2"
                      style={{
                        border: "none",
                        outline: "none",
                        minHeight: "27px",
                        cursor: "text",
                        ...promptInputScrollStyle
                      }}
                      onClick={() => longclipClassInputRef.current?.focus()}
                      role="presentation"
                    >
                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        {(longclipClassNames || []).map((name, idx) => (
                          <span
                            key={`${name}-${idx}`}
                            className="badge-border px-2 py-1 rounded d-flex align-items-center gap-2"
                            style={{ lineHeight: 1 }}
                          >
                            <span>{name}</span>
                            <button
                              type="button"
                              className="p-0 bg-transparent border-0"
                              onClick={() => removeLongclipClassAt(idx)}
                              aria-label={`Remove ${name}`}
                            >
                              <FontAwesomeIcon className="text" icon={faTimesCircle} />
                            </button>
                          </span>
                        ))}
                        <input
                        className="text"
                          ref={longclipClassInputRef}
                          value={longclipClassDraft}
                          onChange={(e) => {
                            const next = e.target.value;
                            if (!next.includes(",")) {
                              setLongclipClassDraft(next);
                              return;
                            }

                            const tokens = tokenizeLongclipClasses(next);
                            const endsWithComma = next.endsWith(",");
                            const complete = endsWithComma ? tokens : tokens.slice(0, -1);
                            const remainder = endsWithComma ? "" : (tokens[tokens.length - 1] || "");

                            if (complete.length) {
                              setLongclipClassesAndSyncPrompt([
                                ...(longclipClassNames || []),
                                ...complete
                              ]);
                            }
                            setLongclipClassDraft(remainder);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === ",") {
                              e.preventDefault();
                              commitLongclipDraft();
                              return;
                            }

                            if (e.key === "Backspace" && !longclipClassDraft) {
                              if ((longclipClassNames || []).length > 0) {
                                e.preventDefault();
                                removeLongclipClassAt((longclipClassNames || []).length - 1);
                              }
                              return;
                            }

                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              if (isLocked) return;

                              const nextClasses = commitLongclipDraft();
                              const compiledPrompt = (nextClasses || []).join(",");
                              const longclipError = getLongclipPromptError(compiledPrompt);
                              if (longclipError) {
                                showInfoPopover("llm-prompt-step", longclipError, 3000);
                                return;
                              }

                              if (tokensExceeded) {
                                showInfoPopover("llm-prompt-step", tokensShortMsg, 3000);
                                return;
                              }

                              attemptStartEvaluation(compiledPrompt);
                            }
                          }}
                          placeholder={
                            (longclipClassNames || []).length
                              ? "Add another class name…"
                              : "Upload images and enter comma-separated class names (e.g. person, animal)…"
                          }
                          style={{
                            flex: 1,
                            minWidth: 180,
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            padding: 0
                          }}
                        />
                      </div>
                    </div>
                  ) : (
                    <textarea
                      ref={promptTextareaRef}
                      className="w-100 mb-2"
                      name="promptText"
                      style={{
                        border: "none",
                        outline: "none",
                        minHeight: `${PROMPT_TEXTAREA_MIN_HEIGHT}px`,
                        maxHeight: `${PROMPT_TEXTAREA_MAX_HEIGHT}px`,
                        resize: "none",
                        overflowY: "auto",
                        overflowX: "hidden"
                      }}
                      value={promptText}
                      placeholder={composerPlaceholder}
                      onChange={handleChange}
                      onKeyDown={handleEnterSubmitstart}
                    />
                  )}

                </div>



                <div className="d-flex justify-content-between llm-composer-toolbar" style={{ alignItems: isMultiline ? "end" : "center" }}>

                  <div className="d-a gap-2 flex-wrap llm-composer-toolbar__left">

                    {supportsImage && (
                      <div id="llm-upload-step" ref={uploadRef}>
                        <StyledTooltip title="Upload Image file">
                          <span className="d-flex">
                            <Button
                              type="button"
                              className="p-1 cursor-pointer badge-border bg-transparent llm-composer-upload-btn"
                              onClick={handleFileSelect}
                            >
                              <UploadIcon className="wf-icon" />
                            </Button>
                          </span>
                        </StyledTooltip>
                      </div>
                    )}
                    <div id="llm-hardware-step" ref={hardwareRef}>
                      <EvaluationTargetHardwareMenu
                        devices={data?.devices}
                        selectedFamily={selectedFamily}
                        onFamilyChange={setSelectedFamily}
                        selectedChipset={selectedChipset}
                        onChipsetChange={setSelectedChipset}
                        selectedDevice={selectedDevice}
                        onDeviceChange={setSelectedDevice}
                        getDeviceStatus={getDeviceStatus}
                        disabled={loadButton || Creating || isLocked}
                        familyRef={familyRef}
                        chipsetRef={chipsetRef}
                        panelPlacement="below"
                        panelAlign="right"
                        useDropdownSelections
                      />
                    </div>
                  </div>




                  <div ref={startBtnRef} className="llm-composer-toolbar__send">
                    <Button
                      type="button"
                      className={`p-1 ps-2 cursor-pointer llm-composer-send-btn llm-composer-start-button ${loadButton ? "busy-border" : ""}`}
                      onClick={handleStartRunClick}
                      disabled={loadButton || Creating || isLocked}
                      aria-label="Start evaluation"
                      title="Start Evaluation"
                    >
                      <SendMessageIcon className="wf-icon" style={{ width: "23px", height: "25px" }} />
                    </Button>
                  </div>


                </div>
              </form>
            </div>
            {renderTryExamples()}

          </div>
        ) : (
          <>
            {(session?.history?.length > 0 &&
              !showReentryGenerationProgress &&
              !reentryHistoryHydrating &&
              !historyError) ? (
              <div
                className={`llm-chat-history-wrap w-100 d-flex flex-column${useGlobalScroll ? " llm-chat-history-wrap--results" : " m-auto flex-fill align-items-center justify-content-between"}`}
                style={useGlobalScroll ? undefined : { minHeight: 0 }}
              >
                <div
                  ref={useGlobalScroll ? undefined : scrollRef}
                  onScroll={useGlobalScroll ? undefined : handleScroll}
                  className={`de-width-chat w-100${useGlobalScroll ? " llm-chat-history--global-scroll" : " h-100 overflow-auto"}`}
                >

                  {/* ============================================================
                HISTORY
            ============================================================= */}
                  {isZeroShotTask ? (
                    <>
                      {/* ---------------------- RIGHT SIDE ---------------------- */}
                      {session.history?.some(
                        (e) =>
                          (e.images && e.images.length > 0) ||
                          e.imagePrompt?.[0]?.prompt
                      ) && (
                          <div className="d-flex justify-content-end">
                            <div
                              style={{ width: "60%", minWidth: 0, alignItems: "flex-end" }}
                              className="d-flex flex-column"
                            >
	                              {/* Thumbnails */}
	                              <div className="d-a flex-wrap gap-2">
	                                {session.history[0]?.images?.map((img, j) => (
	                                  <LongclipBubblePreview
	                                    key={`${img?.imageName || img?.imageUrl}-${j}`}
	                                    img={img}
	                                    imageKey={`${img?.imageName || img?.imageUrl}-${j}`}
	                                    onOpen={openImage}
	                                  />
	                                ))}
	                              </div>

                              <div>
                                {/* User prompt */}
                                {session.history?.[0]?.prompt && !session.history?.[0]?.imagePrompt?.[0]?.prompt && (
                                  renderSubmittedPrompt(session.history[0].prompt)
                                )}

                                {/* Model-generated combined labels */}
                                {session.history?.[0]?.imagePrompt?.[0]?.prompt && (
                                  renderSubmittedPrompt(session.history[0].imagePrompt[0].prompt)
                                )}
                              </div>

                            </div>
                          </div>
                        )}

                      {/* ---------------------- LEFT SIDE ---------------------- */}

                      {session.history?.map((entry, ei) => (
                        <div key={ei} className="mb-4 pt-3 llm-zero-shot-response-turn">

                          <div
                            className="d-flex justify-content-start flex-column mt-2"
                            style={{ maxWidth: "70%" }}
                          >

                            {/* ===========================================
          CASE 1 — ANALYZING (NO IMAGES YET)
         =========================================== */}
                            {entry.response === STREAM_STATUS.ANALYSING && session.history.length === 1 && (
                              <div className="p-2">
                                <DeepEdgeAnalysingIndicator />
                              </div>
                            )}
                            {/* ===========================================
          CASE 2 — SHOW IMAGES + PREDICTIONS
         =========================================== */}
                            {entry.response !== STREAM_STATUS.ANALYSING &&
                              entry.images?.map((img, ii) => {
                                const key = `${ei}-${ii}`;
                                // if (!busyPrmot && busy) {
                                //   setBusyPrompt(true);
                                // }

	                                return (
	                                  <div key={ii} className="mb-4">
	                                    <div style={{ width: "50%" }}>
	                                      <LongclipResponsePreview
	                                        img={img}
	                                        imageKey={key}
	                                        taskType={taskType}
	                                        modelName={modelName}
	                                        onImageLoad={handleImageLoad}
	                                        onOpen={openImage}
	                                      />
	                                    </div>

                                    {/* SCORES */}
                                    {loadedImages[key] && !isZeroShotDetection &&
                                      img.predictions?.some((pred) => String(pred?.label || "").trim()) && (
                                        <div>
                                          {img.predictions
                                            ?.filter((pred) => String(pred?.label || "").trim())
                                            .map((pred, pidx) => (
                                              <div key={pidx}>
                                                <strong>{pred.label}</strong> — {pred.score}
                                              </div>
                                            ))}
                                        </div>
                                      )}
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      ))}

                    </>
                  ) : (
                    /* ============================================================
                       VQA & TEXT GENERATION TASKS
                    ============================================================= */
                    <>
                      {session?.history?.map((entry, i) => {

                        return (
                          <div key={i} className="llm-chat-turn">
                            {Array.isArray(entry.local_images) && entry.local_images.length > 0 && (
                              <div className="d-flex justify-content-end mb-1 w-75 ms-auto gap-2 flex-wrap">
                                {entry.local_images.map((img, idx) => (
                                  <div key={idx}>
                                    {img.type === "video" ? (
                                      <video
                                        src={img.url}
                                        controls
                                        preload="metadata"
                                        style={{
                                          width: "150px",
                                          height: "150px",
                                          borderRadius: "8px",
                                          objectFit: "cover",
                                          background: "#000",
                                          cursor: "pointer",
                                        }}
                                        onClick={() => openImage(img.url)}
                                      />
                                    ) : (
                                      <LlmBubbleImagePreview
                                        src={img.base64 || img.url}
                                        thumbUrl={img.thumbUrl}
                                        alt={img.name || "input image"}
                                        onOpen={openImage}
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* SINGLE IMAGE (legacy support) */}
                            {entry.local_image && !entry.local_images && (
                              <div className="d-flex justify-content-end mb-1 w-75 ms-auto">
                                <LlmBubbleImagePreview
                                  src={entry.local_image}
                                  alt={entry.imageName || "input image"}
                                  onOpen={openImage}
                                />
                              </div>
                            )}


                            {/* Server image_url/image_url[] (only for VQA history) */}
                            {getImageUrls(entry.image_urls?.length ? entry.image_urls : entry.image_url).length > 0 &&
                              !entry.local_image &&
                              !(isFlorence && Array.isArray(entry.local_images) && entry.local_images.length > 0) && (
                              <div className="d-flex justify-content-end mb-1 w-75 ms-auto gap-2 flex-wrap">
                                {getImageUrls(entry.image_urls?.length ? entry.image_urls : entry.image_url)
                                  .map((imageUrl, imageIndex) => (
                                    <LlmBubbleImagePreview
                                      key={`${imageUrl}-${imageIndex}`}
                                      src={imageUrl}
                                      alt={entry.imageName || `input image ${imageIndex + 1}`}
                                      onOpen={openImage}
                                    />
                                  ))}
                              </div>
                            )}

                            {/* USER PROMPT */}
                            {entry.prompt && <div className="d-flex justify-content-end w-75 ms-auto llm-chat-prompt-row">
                              <div className="p-2 badge-border llm-chat-prompt-bubble">{entry.prompt ? entry.prompt : promptDuplicate}</div>
                            </div>}

                            {/* AI RESPONSE */}
                            <div className="d-flex justify-content-start mt-2 llm-chat-response-row" style={{ maxWidth: '90%' }}>
                              <div
                                className="p-2 llm-chat-response-content"
                                style={{ maxWidth: "100%", whiteSpace: "pre-wrap" }}
                              >
                                {entry.florenceSpatial ? (
                                  <FlorenceSpatialPreview
                                    spatial={entry.florenceSpatial}
                                    onOpen={openFlorenceImage}
                                  />
                                ) : (isVideoEntry(entry) && Array.isArray(entry.videoFrames) && entry.videoFrames.length > 0) ? (
                                  <div className="d-flex flex-column gap-3">
                                    {entry.videoFrames.map((frameItem, frameIdx) => (
                                      <div key={`${frameItem.frame || "frame"}-${frameIdx}`} style={{ maxWidth: "420px" }}>
                                        {frameItem.frame && (
                                          <img
                                            src={frameItem.frame}
                                            alt={`video-frame-${frameIdx + 1}`}
                                            style={{
                                              width: "100%",
                                              maxWidth: "360px",
                                              borderRadius: "8px",
                                              objectFit: "cover",
                                              marginBottom: "8px",
                                              cursor: "pointer",
                                            }}
                                            onClick={() => openImage(frameItem.frame)}
                                          />
                                        )}
                                        <div style={{ whiteSpace: "pre-wrap" }}>
                                          {frameItem.response || (!frameItem.done ? <DeepEdgeAnalysingIndicator /> : "")}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (entry.response === STREAM_STATUS.ANALYSING && evaluationStatusMap.status !== "Complete" && evaluationStatusMap.status !== "Terminated") ? (
                                  <DeepEdgeAnalysingIndicator />
                                ) : (entry.response === STREAM_STATUS.ANALYSING && (evaluationStatusMap.status === "Complete" || evaluationStatusMap.status === "Terminated")) ? (
                                  <div className="d-a gap-2">
                                    <div>

                                    </div> <p >No Response</p>
                                  </div>
                                ) : (entry.response === NO_RESPONSE_MESSAGE) ? (
                                  <div className="d-a gap-2">
                                    <div>

                                    </div> <p >No Response</p>
                                  </div>
                                ) : (entry.retry) ? (
                                  <div>
                                    {entry.errorMessage && (
                                      <div className="text-danger small mb-2">
                                        {entry.errorMessage}
                                      </div>
                                    )}
                                    <span
                                      className="text-primary cursor-pointer"
                                      onClick={() => {
                                      handlepredictions(
                                        eval_id,
                                        entry.prompt,
                                        null,
                                        entry.imageName || "none",
                                        entry.local_images ||
                                        (entry.local_image
                                          ? [{ base64: entry.local_image, name: entry.imageName }]
                                          : []),
                                        "retry",
                                        entry.turnId
                                      );

                                      }}

                                    >
                                      Retry <FullCycleIcon className="wf-icon-small" />
                                    </span>
                                  </div>
                                ) : (
                                  (() => {
                                    const rawText = normalizeLLMResponse(entry.response || "");

                                    // ------------------------------------
                                    // 1️⃣ Split into TEXT / CODE segments
                                    // ------------------------------------

                                    const segments = buildMarkdownSegments(rawText);
                                    
                                    // ------------------------------------
                                    // 2️⃣ Render segments
                                    // ------------------------------------
                                    return segments.map((seg, idx) => {
                                      if (!seg?.content?.trim()) return null;
                                      /* ================================
                                         TEXT SEGMENT
                                      ================================ */
                                      if (seg.type === "text") {
                                        
                                        const lines = seg.content.split("\n");

                                        return (
                                          <div key={idx}>
                                            {lines.map((line, lineIdx) => {
                                              
                                              if (line.startsWith("### ")) {
                                                return (
                                                  <div
                                                    key={lineIdx}
                                                    className="fw-semibold mt-3 mb-1"
                                                    style={{
                                                      borderLeft: "3px solid var(--leading-color)",
                                                      paddingLeft: "8px",
                                                    }}
                                                  >
                                                    {line.replace(/^###\s*/, "")}
                                                  </div>
                                                );
                                              }

                                              // Empty line → spacing
                                              if (!line.trim()) {
                                                return <br key={lineIdx} />;
                                              }

                                              // Normal text
                                              return (
                                                <div
                                                  key={lineIdx}
                                                  dangerouslySetInnerHTML={{
                                                    __html: parseStreamingMarkdown(line),
                                                  }}
                                                />
                                              );
                                            })}
                                          </div>
                                        );
                                      }

                                      /* ================================
                                         CODE SEGMENT
                                         (ALL ``` ``` → SAME UI)
                                      ================================ */

                                      let displayContent = seg.content;

                                      
                                      try {
                                        const parsed = JSON.parse(seg.content.trim());
                                        displayContent = JSON.stringify(parsed, null, 2);
                                      } catch {
                                        
                                      }

                                      return (
                                        <pre
                                          key={idx}
                                          className="container-card"
                                          style={{
                                            whiteSpace: "pre-wrap",
                                            overflowX: "auto",
                                          }}
                                        >
                                          {displayContent}
                                        </pre>
                                      );
                                    });
                                  })()
                                )}




                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </>
                  )}

                  {/* ============================================================
                LOADING BUBBLE
            ============================================================= */}
                  {session.loading &&
                    session.pendingPrompt &&
                    (!supportsContextTabs || streamingContext === activeContext) &&
                    !session.history?.some(h => h.response === STREAM_STATUS.ANALYSING) && (
                      <div className="">

                        {/* SHOW IMAGE WHEN PENDING (VQA) */}
                        {isVisualQuestionAnswering &&
                          (session.selectedImages?.[0]?.base64 ||
                            session.chatImages?.[0]?.base64) && (
                            <div className="d-flex justify-content-end mb-1 w-75 ms-auto">
                              <LlmBubbleImagePreview
                                src={
                                  session.selectedImages?.[0]?.base64 ||
                                  session.chatImages?.[0]?.base64
                                }
                                thumbUrl={
                                  session.selectedImages?.[0]?.thumbUrl ||
                                  session.chatImages?.[0]?.thumbUrl
                                }
                                alt={
                                  session.selectedImages?.[0]?.name ||
                                  session.chatImages?.[0]?.name ||
                                  "input image"
                                }
                                onOpen={openImage}
                              />
                            </div>
                          )}
                      </div>
                    )}
                </div>
              </div>
            ) : ((loadingHistory || activeContextStreamWait) ?
              <div className="d-flex justify-content-center align-items-center py-3">
                {showReentryGenerationProgress ? (
                  // <DeepEdgeAnalysingIndicator message="Generation is in progress. Please hold on and do not refresh the page." />
                <div className="d-a gap-2 text-muted">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" fill="none">
                                      <path
                                        d="M23 3h-9C7.925 3 3 7.925 3 14v0c0 6.075 4.925 11 11 11h9M21 14h7"
                                        stroke="#3755d3"
                                        className="path"
                                        strokeWidth="6"
                                        strokeLinecap="round"
                                      />
                                    </svg>
                                    <p className="m-0 p-0">Generation is in progress. Please hold on and do not refresh the page.</p>
                                  </div>
                ) : (
                  <>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="50"
                      height="26"
                      viewBox="0 0 31 28"
                      fill="none"
                    >
                      <path
                        d="M23 3h-9C7.925 3 3 7.925 3 14v0c0 6.075 4.925 11 11 11h9M21 14h7"
                        stroke="#3755d3"
                        className="path"
                        strokeWidth="6"
                        strokeLinecap="round"
                      ></path>
                    </svg>
                    <span className="ms-2 text-muted">Loading the evaluation. This may take a few moments.</span>
                  </>
                )}

              </div>
              : <>{historyError ? <div className="d-j-a h-100 flex-column py-4 text-center text-muted">
                <div className="fw-semibold">
                  No predictions available yet
                </div>
                <div className="small mt-2">
                  If you believe this is unexpected, it may be an issue on our side.
                </div>

                <Button
                  size="sm"
                  className="mt-3"
                  color="primary"
                  onClick={() => {
                    setLoadingHistory(true);
                    handleprevpredictions(eval_id, 1);
                  }}
                >
                  Retry
                </Button>
              </div> : (isEvaluating && isLatestContext) ? (
                <div className="h-100" aria-label="New context ready" />
              ) : <div className="d-j-a h-100 flex-column py-4 text-center text-muted">
                <div className="fw-semibold">
                  No results in this evaluation
                </div>
              </div>}</>)}</>
        )}

      </div>


      {/* ================================================================
              INPUT AREA
          ================================================================= */}
      {!evaluationCompleted && chatBox && (!supportsContextTabs || isLatestContext) && !(
        isFlorence && String(currentStatus).trim().toLowerCase() === "evaluating"
      ) && (

        <div
          ref={promptRef}
          id="llm-prompt-step"
          className={`de-width-chat w-100 py-0 pt-3 llm-context-content${useGlobalScroll ? " llm-chat-composer--global-scroll" : ""}`}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleEvaluateContinue("prompt");
            }}
            className="w-100 container-card bg-transparent p-2 p-sm-3 pb-1 pb-sm-1 shadow"
            style={composerFormStyle}
          >

            {renderSelectedImageThumbs()}

            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="image/*"
              onChange={(e) => {
                const replace = replaceImagesOnSelectRef.current;
                replaceImagesOnSelectRef.current = false;
                processFiles(e.target.files, { replace });
              }}
            />


            <div className="d-flex gap-2 mb-2 llm-composer-input-row align-items-center">

              <textarea
                ref={promptTextareaRef}
                className="w-100"
                style={{
                  border: "none",
                  outline: "none",
                  minHeight: `${PROMPT_TEXTAREA_MIN_HEIGHT}px`,
                  maxHeight: `${PROMPT_TEXTAREA_MAX_HEIGHT}px`,
                  resize: "none",
                  overflowY: "auto",
                  overflowX: "hidden"
                }}
                name="promptText"
                value={promptText}
                placeholder={composerPlaceholder}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isLocked) {
                    handleEnterSubmit(e);
                  }
                }}
              />
            </div>



            <div className="d-flex justify-content-between llm-composer-toolbar" style={{ alignItems: isMultiline ? "end" : "center" }}>

              <div className="d-a gap-2 flex-wrap llm-composer-toolbar__left">

                {supportsImage && (
                  <div id="llm-upload-step" ref={uploadRef}>
                    <StyledTooltip title="Upload Image file">
                      <span className="d-flex">
                        <Button
                          type="button"
                          className="p-1 cursor-pointer badge-border bg-transparent llm-composer-upload-btn"
                          onClick={handleFileSelect}
                        >
                          <UploadIcon className="wf-icon" />
                        </Button>
                      </span>
                    </StyledTooltip>
                  </div>
                )}
                <div id="llm-hardware-step" ref={hardwareRef} className="d-a justify-content-between mt-1 ps-2">
                  <span className="eval-form-info px-2 rounded d-a gap-2 text-capitalize">
                    <ChipsetIcon className="wf-icon-small" />
                    {selectedFamily} • {selectedDeviceInfo}
                  </span>
                </div>

              </div>




              {hasInput && (
                <Button
                  type="submit"
                  className={`d-j-a p-1 ps-2 cursor-pointer llm-composer-send-btn grey-card3 ${busy ? "busy-border" : ""}`}
                  style={{ borderRadius: "50%", border: "5px double var(--glass-color)" }}
                  disabled={message === "Queued" || isLocked}
                >
                  <SendMessageIcon
                    className="wf-icon"
                    style={{ width: "23px", height: "25px" }}
                  />
                </Button>
              )}


            </div>
          </form>
          {showContextExamples && renderTryExamples()}
        </div>
      )}

      </div>
      </div>

      {isDragging && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            background: "rgba(0, 0, 0, 0.45)",
            backdropFilter: "blur(3px)",
            border: "3px dashed #4caf50",
            zIndex: 999999,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
            fontSize: "28px",
            pointerEvents: "none"
          }}
        >
          {isDragging && (
            <div>
              {isVisualQuestionAnswering
                ? "Drop an image here"
                : "Drop ZIP or Images Anywhere"}
            </div>
          )}

        </div>
      )}


      <Popover
        key={infoPopover.target}
        isOpen={infoPopover.open}
        target={infoPopover.target}
        placement="top"
        toggle={hideInfoPopover}
      >
        <PopoverBody>{infoPopover.message}</PopoverBody>
      </Popover>





      <Modal
        style={{ width: 'fit-content' }}
        isOpen={showImageModal}
        toggle={() => setShowImageModal(false)}
        centered
        size="lg"
      >
        <div className="p-2 text-end" style={{ position: 'absolute', top: '0', right: '0' }}>
          <button className="close-btn" onClick={() => setShowImageModal(false)}>
            <CloseIcon className="wf-icon text-secondary" />
          </button>
        </div>

        <div className="text-center">

          {modalFlorenceSpatial ? (
            <FlorenceSpatialPreview
              spatial={modalFlorenceSpatial}
              isModal
            />
          ) : (
            <ImageCanvas
              key={`${modalImage}-${modelName}`}
              img={{
                url: modalImage,
                boxes: boundingboxes,
                // landmarks: img.landmarks,
                score: owlvitscore
              }}
              modelName={modelName}
              isModal={true}
            />
          )}
        </div>
      </Modal>



    </div>
  );
}
